import { createSlice } from '@reduxjs/toolkit';
import { polygonToRects, edgePointAt } from '../utils/freeWallRoom';

// ─── FURNITURE TYPES ─────────────────────────────────────────────────────────
// h3 = reference vertical height in metres (3D view only — every furniture
// shape is hand-modeled with fixed internal proportions built up from this
// as a baseline, then scaled as a whole to match item.h3; see FurnitureMesh3D).
export const FURNITURE_TYPES = [
  { type: 'sofa',      label: 'Sofa',         icon: '🛋️', w: 104, h: 52,  color: '#a89070', h3: 0.85 },
  { type: 'armchair',  label: 'Armchair',     icon: '💺', w: 52,  h: 52,  color: '#987858', h3: 0.85 },
  { type: 'table',     label: 'Dining Table', icon: '🪑', w: 80,  h: 80,  color: '#c8a870', h3: 0.75 },
  { type: 'chair',     label: 'Chair',        icon: '🪑', w: 30,  h: 32,  color: '#a08060', h3: 0.85 },
  { type: 'bed',       label: 'Bed',          icon: '🛏️', w: 84,  h: 104, color: '#c8b898', h3: 0.55 },
  { type: 'desk',      label: 'Desk',         icon: '💻', w: 78,  h: 44,  color: '#b89870', h3: 0.75 },
  { type: 'tv',        label: 'TV',           icon: '📺', w: 78,  h: 14,  color: '#2a2a38', h3: 0.60 },
  { type: 'shelf',     label: 'Bookshelf',    icon: '📚', w: 78,  h: 24,  color: '#8b6030', h3: 1.80 },
  { type: 'wardrobe',  label: 'Storage',      icon: '🗄️', w: 90,  h: 58,  color: '#6b4a32', h3: 2.00 },
  { type: 'bathtub',   label: 'Bathtub',      icon: '🛁', w: 78,  h: 40,  color: '#e0ecf0', h3: 0.55 },
  { type: 'toilet',    label: 'Toilet',       icon: '🚽', w: 36,  h: 52,  color: '#f0f0ec', h3: 0.40 },
  { type: 'sink',      label: 'Sink',         icon: '🚿', w: 36,  h: 36,  color: '#e8ecf0', h3: 0.85 },
  { type: 'lamp',      label: 'Floor Lamp',   icon: '💡', w: 24,  h: 24,  color: '#d4c090', h3: 1.50 },
  { type: 'plant',     label: 'Plant',        icon: '🌿', w: 26,  h: 26,  color: '#6aaa60', h3: 0.90 },
  { type: 'rug',       label: 'Rug',          icon: '🟫', w: 104, h: 78,  color: '#c09878', h3: 0.02 },
];

// ─── LIGHTING TYPES (ceiling-mounted fixtures — 3D room view only) ─────────────
// Free-placed like furniture (flat x/y in pixels) but height-locked to the
// ceiling plane instead of the floor — see moveLighting/LightingMesh3D.
export const LIGHTING_TYPES = [
  { type: 'chandelier',   label: 'Modern Chandelier',    icon: '💡', w: 56, h: 56, color: '#c9a227' },
  { type: 'ceiling_lamp', label: 'Minimalist Ceiling Lamp', icon: '⚪', w: 42, h: 42, color: '#f5f0e6' },
];

// ─── WALL DECOR (openings-list items that mount on a wall face but, unlike
// doors/windows/archways, don't cut a void — the wall stays solid behind them) ─
export const WALL_DECOR_TYPES = ['painting', 'ac', 'clock', 'outlet', 'radiator'];
export const isWallDecorType = (type) => WALL_DECOR_TYPES.includes(type);

export const WALL_DECOR_META = {
  painting: { label: 'Painting',       icon: '🖼️' },
  ac:       { label: 'AC Unit',        icon: '❄️' },
  clock:    { label: 'Wall Clock',     icon: '🕐' },
  outlet:   { label: 'Power Outlet',   icon: '🔌' },
  radiator: { label: 'Radiator',       icon: '♨️' },
};

// Default size/elevation/color per opening type — shared by addOpening,
// addWallSegOpening and addAutoWallOpening so all three placement paths
// (per-room, standalone wall, Builder auto-wall) agree on the same defaults.
const OPENING_TYPE_DEFAULTS = {
  door:     { width: 0.88, height: 2.06,               color: '#c4a87a' },
  archway:  { width: 1.2,  height: 2.4,                color: '#e0d8c8' },
  window:   { width: 0.98, height: 0.88, sill: 0.9,    color: '#e8e0d0' },
  painting: { width: 0.80, height: 0.60, sill: 1.30,   color: '#8a6a45' },
  ac:       { width: 0.85, height: 0.30, sill: 2.15,   color: '#f2f2ef' },
  clock:    { width: 0.35, height: 0.35, sill: 1.55,   color: '#2a2622' },
  outlet:   { width: 0.10, height: 0.10, sill: 0.30,   color: '#f2f2ef' },
  radiator: { width: 1.00, height: 0.50, sill: 0.12,   color: '#f2f2ef' },
};
function openingTypeDefaults(type) {
  return OPENING_TYPE_DEFAULTS[type] || OPENING_TYPE_DEFAULTS.window;
}

// Six selectable 3D designs per opening/wall-item type — mirrors the
// furniture "style" system (e.g. floor lamp: Floor/Arc/Tripod/...). The
// first entry in each list is always the pre-existing default design, so
// openings saved before this feature (no `style` field) keep rendering
// exactly as before.
export const OPENING_STYLES = {
  // Doors are GLB models now (door1…door21). The 3D views render GLB_DOORS[style];
  // legacy saved doors (panel4/flush/…) still render via their procedural branch.
  door: Array.from({ length: 21 }, (_, i) => ({ id: `door${i + 1}`, label: `Door ${i + 1}` })),
  // Windows are GLB models now (window1…window15). Legacy saved windows
  // (doublehung/casement/…) still render via their procedural branch.
  window: Array.from({ length: 15 }, (_, i) => ({ id: `window${i + 1}`, label: `Window ${i + 1}` })),
  archway: [
    { id: 'round',      label: 'Round Arch' },
    { id: 'gothic',     label: 'Gothic Arch' },
    { id: 'flat',       label: 'Flat Cased' },
    { id: 'elliptical', label: 'Elliptical Arch' },
    { id: 'roman',      label: 'Roman Keystone' },
    { id: 'modern',     label: 'Modern Minimal' },
  ],
  painting: [
    { id: 'abstract',  label: 'Abstract' },
    { id: 'baroque',   label: 'Ornate Baroque' },
    { id: 'modern',    label: 'Modern Black' },
    { id: 'canvas',    label: 'Gallery Canvas' },
    { id: 'collage',   label: 'Photo Collage' },
    { id: 'landscape', label: 'Landscape Oil' },
  ],
  ac: [
    { id: 'split',    label: 'Split Unit' },
    { id: 'cassette', label: 'Cassette' },
    { id: 'curved',   label: 'Curved Premium' },
    { id: 'window',   label: 'Window Unit' },
    { id: 'slim',     label: 'Slim High-Wall' },
    { id: 'retro',    label: 'Retro Boxy' },
  ],
  // Clocks are GLB models now (clock1…clock13). Legacy saved clocks
  // (analog/digital/…) still render via their procedural branch.
  clock: Array.from({ length: 13 }, (_, i) => ({ id: `clock${i + 1}`, label: `Clock ${i + 1}` })),
  outlet: [
    { id: 'eu',         label: 'EU Round-Pin' },
    { id: 'us',         label: 'US Decora' },
    { id: 'uk',         label: 'UK 3-Pin' },
    { id: 'usb',        label: 'USB Combo' },
    { id: 'brass',      label: 'Brass Floor' },
    { id: 'industrial', label: 'Industrial Metal' },
  ],
  radiator: [
    { id: 'panel',    label: 'Panel Convector' },
    { id: 'castiron', label: 'Cast Iron Column' },
    { id: 'towel',    label: 'Towel Rail' },
    { id: 'vertical', label: 'Vertical Designer' },
    { id: 'skirting', label: 'Low Skirting' },
    { id: 'electric', label: 'Electric Flat Panel' },
  ],
};

// ─── ROOM TYPES (pre-sized blocks) ───────────────────────────────────────────
export const ROOM_TYPES = [
  { type: 'living',    label: 'Living Room',     w: 4, h: 3, icon: '🛋️',  color: '#ddd5c8' },
  { type: 'kitchen',   label: 'Kitchen',         w: 3, h: 2, icon: '🍳',  color: '#d4ccbd' },
  { type: 'bedroom',   label: 'Bedroom',         w: 3, h: 3, icon: '🛏️',  color: '#cfc7ba' },
  { type: 'bathroom',  label: 'Bathroom',        w: 2, h: 2, icon: '🚿',  color: '#ccd4d4' },
  { type: 'hallway',   label: 'Hallway',         w: 1, h: 3, icon: '🚪',  color: '#d0cec5' },
  { type: 'garage',    label: 'Garage',          w: 3, h: 2, icon: '🚗',  color: '#c8c4bc' },
  { type: 'office',    label: 'Home Office',     w: 2, h: 2, icon: '💻',  color: '#c8d0cc' },
  { type: 'dining',    label: 'Dining Room',     w: 3, h: 2, icon: '🍽️',  color: '#d4cdc2' },
  { type: 'custom',   label: 'Room',            w: 3, h: 3, icon: '⬜',   color: '#d6d0c4' },
];

// ─── MATERIALS ────────────────────────────────────────────────────────────────
export const FLOOR_MATERIALS = [
  // ── WOOD ──────────────────────────────────────────────────────────────────
  { id: 'oak',         label: 'Oak Planks',       category: 'wood',   baseColor: '#c8956c', grainColor: '#a87448' },
  { id: 'walnut',      label: 'Dark Walnut',       category: 'wood',   baseColor: '#6b4226', grainColor: '#4a2c18' },
  { id: 'maple',       label: 'Light Maple',       category: 'wood',   baseColor: '#e0c99a', grainColor: '#c8ad78' },
  { id: 'dark_oak',    label: 'Dark Oak',          category: 'wood',   baseColor: '#4a2e18', grainColor: '#361e0e' },
  { id: 'limed_oak',   label: 'Limed Oak',         category: 'wood',   baseColor: '#d4c8b4', grainColor: '#b8a898' },
  { id: 'pine',        label: 'Pine',              category: 'wood',   baseColor: '#e8c080', grainColor: '#d0a050' },
  { id: 'herringbone', label: 'Herringbone',       category: 'wood',   baseColor: '#c0885a', grainColor: '#a86838' },
  { id: 'chevron',     label: 'Chevron',           category: 'wood',   baseColor: '#b87848', grainColor: '#9c5830' },
  { id: 'parquet',     label: 'Parquet',           category: 'wood',   baseColor: '#c89060', grainColor: '#b07040' },
  { id: 'bamboo',      label: 'Bamboo',            category: 'wood',   baseColor: '#dcc878', grainColor: '#c0aa50' },
  // ── NATURAL STONE — random opus + crazy paving, real riven/tumbled surface ──
  { id: 'stn_travertine',  label: 'Travertine',        category: 'stone', baseColor: '#e6dabd', stoneStyle: 'tumbled', cell: 150, toneVary: 15, rough: 0.50, groutColor: '#cbb799' },
  { id: 'stn_silver_trav', label: 'Silver Travertine', category: 'stone', baseColor: '#c6c2b6', stoneStyle: 'tumbled', cell: 150, toneVary: 15, rough: 0.50, groutColor: '#a6a298' },
  { id: 'stn_limestone',   label: 'Ivory Limestone',   category: 'stone', baseColor: '#e6dfce', stoneStyle: 'tumbled', cell: 140, toneVary: 13, rough: 0.55, groutColor: '#c9c1ac' },
  { id: 'stn_sandstone',   label: 'Sandstone',         category: 'stone', baseColor: '#d8c398', stoneStyle: 'sand',    cell: 152, toneVary: 20, rough: 0.62, groutColor: '#b8a074' },
  { id: 'stn_slate',       label: 'Grey Slate',        category: 'stone', baseColor: '#585a5a', stoneStyle: 'riven',   cell: 124, toneVary: 22, rough: 0.42, groutColor: '#303232' },
  { id: 'stn_slate_bl',    label: 'Black Slate',       category: 'stone', baseColor: '#343436', stoneStyle: 'riven',   cell: 124, toneVary: 20, rough: 0.40, groutColor: '#1e1e20' },
  { id: 'stn_bluestone',   label: 'Bluestone',         category: 'stone', baseColor: '#7c8288', stoneStyle: 'riven',   cell: 192, toneVary: 16, rough: 0.50, groutColor: '#565c60' },
  { id: 'stn_flagstone',   label: 'Grey Flagstone',    category: 'stone', baseColor: '#9a968c', stoneStyle: 'riven',   cellN: 6,  toneVary: 26, rough: 0.55, groutColor: '#6c685f' },
  { id: 'stn_flag_autumn', label: 'Autumn Flagstone',  category: 'stone', baseColor: '#a98f6e', stoneStyle: 'sand',    cellN: 6,  toneVary: 30, rough: 0.58, groutColor: '#6e5f4a' },
  // ── PORCELAIN TILE — large-format marble & stone effect (grid + grout, ─────
  //    per-tile veining, polished or matte). The realistic modern floor look.
  { id: 'porc_carrara',   label: 'Carrara White',   category: 'tile', baseColor: '#f2f1ec', veins: '#b8bcc2', tileStyle: 'marble',   polished: true },
  { id: 'porc_statuario', label: 'Statuario White', category: 'tile', baseColor: '#f6f5f1', veins: '#9aa1ab', tileStyle: 'marble',   polished: true },
  { id: 'porc_grey',      label: 'Grey Marble',     category: 'tile', baseColor: '#c2c0bb', veins: '#7c7a78', tileStyle: 'marble',   polished: true },
  { id: 'porc_black',     label: 'Nero Marquina',   category: 'tile', baseColor: '#1b1b1f', veins: '#c2c4ca', tileStyle: 'marble',   polished: true },
  { id: 'porc_cream',     label: 'Sahara Beige',    category: 'tile', baseColor: '#ece2cc', veins: '#c8a878', tileStyle: 'marble',   polished: true },
  { id: 'porc_cloud',     label: 'Cloud Concrete',  category: 'tile', baseColor: '#dcdcd7',                   tileStyle: 'concrete', polished: true, rough: 0.26 },
  { id: 'porc_slate',     label: 'Grey Stone',      category: 'tile', baseColor: '#9c988f',                   tileStyle: 'stone',    rough: 0.55 },
  { id: 'porc_sand',      label: 'Sand Stone',      category: 'tile', baseColor: '#cabfa8',                   tileStyle: 'stone',    rough: 0.5 },
  // Small-square grids (more, littler tiles) — same looks, denser layout.
  { id: 'porc_carrara_sq',label: 'Carrara Grid',    category: 'tile', baseColor: '#f2f1ec', veins: '#b8bcc2', tileStyle: 'marble',   polished: true, tilesN: 4 },
  { id: 'porc_grey_sq',   label: 'Grey Marble Grid',category: 'tile', baseColor: '#c2c0bb', veins: '#7c7a78', tileStyle: 'marble',   polished: true, tilesN: 4 },
  { id: 'porc_stone_sq',  label: 'Grey Stone Grid', category: 'tile', baseColor: '#9c988f',                   tileStyle: 'stone',    rough: 0.55, tilesN: 4 },
  { id: 'porc_sand_sq',   label: 'Sand Stone Grid', category: 'tile', baseColor: '#cabfa8',                   tileStyle: 'stone',    rough: 0.5,  tilesN: 4 },
  { id: 'porc_mosaic',    label: 'Stone Mosaic',    category: 'tile', baseColor: '#b8b4ac',                   tileStyle: 'stone',    rough: 0.5,  tilesN: 8 },
  // ── CARPET ────────────────────────────────────────────────────────────────
  { id: 'carpet_grey', label: 'Grey Carpet',       category: 'carpet', baseColor: '#a8a8a8' },
  { id: 'carpet_beige',label: 'Beige Carpet',      category: 'carpet', baseColor: '#c8b898' },
  { id: 'carpet_sage', label: 'Sage Carpet',       category: 'carpet', baseColor: '#708070' },
  // ── MORE WOOD ────────────────────────────────────────────────────────────
  { id: 'walnut_am',   label: 'American Walnut',   category: 'wood',   baseColor: '#784228', grainColor: '#5a2e18' },
  { id: 'teak',        label: 'Teak',              category: 'wood',   baseColor: '#9a6030', grainColor: '#804818' },
  { id: 'ash',         label: 'Ash Blonde',        category: 'wood',   baseColor: '#d8c8a0', grainColor: '#c0aa78' },
  { id: 'whitewash',   label: 'Whitewashed',       category: 'wood',   baseColor: '#ede8de', grainColor: '#d8d0c0' },
  { id: 'ebony',       label: 'Ebony',             category: 'wood',   baseColor: '#1c1008', grainColor: '#2a1808' },
  { id: 'grey_oak',    label: 'Grey Oak',          category: 'wood',   baseColor: '#9898a0', grainColor: '#787882' },
  // ── MORE CARPET ──────────────────────────────────────────────────────────
  { id: 'carpet_plush',label: 'Plush Cream',       category: 'carpet', baseColor: '#e8e0d0' },
  { id: 'carpet_choc', label: 'Chocolate',         category: 'carpet', baseColor: '#482810' },
  { id: 'carpet_char', label: 'Charcoal',          category: 'carpet', baseColor: '#404040' },
  { id: 'sisal',       label: 'Sisal / Jute',      category: 'carpet', baseColor: '#c8b870' },
  // ── NEW: WOOD / STONE / TILE / CARPET ────────────────────────────────────
  { id: 'hickory',     label: 'Hickory',           category: 'wood',   baseColor: '#c19a5b', grainColor: '#8a6530' },
  { id: 'wenge',       label: 'Wenge',             category: 'wood',   baseColor: '#3a2820', grainColor: '#241611' },
  { id: 'basketweave', label: 'Basketweave',       category: 'wood',   baseColor: '#c4925e', grainColor: '#9c6c3c' },
  { id: 'berber',      label: 'Berber Loop',       category: 'carpet', baseColor: '#b8a888' },
  // ── MODERN: wide-plank oaks + seamless microcement (all high-realism) ─────
  { id: 'white_oak',   label: 'Natural White Oak', category: 'wood',   baseColor: '#dcc8a4', grainColor: '#c0a87e' },
  { id: 'smoked_oak',  label: 'Smoked Oak',        category: 'wood',   baseColor: '#8c7c68', grainColor: '#695a48' },
  { id: 'fumed_oak',   label: 'Fumed Oak',         category: 'wood',   baseColor: '#6b5a48', grainColor: '#4a3c30' },
  // ── LAMINATE — wood-look planks with a wear-layer sheen (large variety) ────
  { id: 'lam_natural',   label: 'Natural Oak',       category: 'laminate', baseColor: '#c49660', grainColor: '#a8783c' },
  { id: 'lam_golden',    label: 'Golden Oak',        category: 'laminate', baseColor: '#b88a50', grainColor: '#986c34' },
  { id: 'lam_rustic',    label: 'Rustic Oak',        category: 'laminate', baseColor: '#9a7850', grainColor: '#6e5030' },
  { id: 'lam_smoked',    label: 'Smoked Oak',        category: 'laminate', baseColor: '#8a7458', grainColor: '#665238' },
  { id: 'lam_greige',    label: 'Greige Oak',        category: 'laminate', baseColor: '#b6ac9a', grainColor: '#968c78' },
  { id: 'lam_driftwood', label: 'Driftwood',         category: 'laminate', baseColor: '#bcb4a6', grainColor: '#9c9284' },
  { id: 'lam_grey',      label: 'Grey Oak',          category: 'laminate', baseColor: '#b0aca6', grainColor: '#8e8a84' },
  { id: 'lam_lgrey',     label: 'Light Grey Oak',    category: 'laminate', baseColor: '#c6c2bc', grainColor: '#a8a49e' },
  { id: 'lam_white',     label: 'Whitewashed Oak',   category: 'laminate', baseColor: '#dcd8d0', grainColor: '#beb9b0' },
  { id: 'lam_charcoal',  label: 'Charcoal Oak',      category: 'laminate', baseColor: '#6a665f', grainColor: '#4a4640' },
  { id: 'lam_walnut',    label: 'Dark Walnut',       category: 'laminate', baseColor: '#4a3020', grainColor: '#2c1a0e' },
  { id: 'lam_walnut_g',  label: 'Walnut High-Gloss', category: 'laminate', baseColor: '#5a3a24', grainColor: '#3a2214' },
  // Same laminates laid the OTHER way (boards/lines run along Y instead of X).
  { id: 'lam_natural_y',   label: 'Natural Oak ↕',       category: 'laminate', baseColor: '#c49660', grainColor: '#a8783c', dir: 'y' },
  { id: 'lam_golden_y',    label: 'Golden Oak ↕',        category: 'laminate', baseColor: '#b88a50', grainColor: '#986c34', dir: 'y' },
  { id: 'lam_rustic_y',    label: 'Rustic Oak ↕',        category: 'laminate', baseColor: '#9a7850', grainColor: '#6e5030', dir: 'y' },
  { id: 'lam_smoked_y',    label: 'Smoked Oak ↕',        category: 'laminate', baseColor: '#8a7458', grainColor: '#665238', dir: 'y' },
  { id: 'lam_greige_y',    label: 'Greige Oak ↕',        category: 'laminate', baseColor: '#b6ac9a', grainColor: '#968c78', dir: 'y' },
  { id: 'lam_driftwood_y', label: 'Driftwood ↕',         category: 'laminate', baseColor: '#bcb4a6', grainColor: '#9c9284', dir: 'y' },
  { id: 'lam_grey_y',      label: 'Grey Oak ↕',          category: 'laminate', baseColor: '#b0aca6', grainColor: '#8e8a84', dir: 'y' },
  { id: 'lam_lgrey_y',     label: 'Light Grey Oak ↕',    category: 'laminate', baseColor: '#c6c2bc', grainColor: '#a8a49e', dir: 'y' },
  { id: 'lam_white_y',     label: 'Whitewashed Oak ↕',   category: 'laminate', baseColor: '#dcd8d0', grainColor: '#beb9b0', dir: 'y' },
  { id: 'lam_charcoal_y',  label: 'Charcoal Oak ↕',      category: 'laminate', baseColor: '#6a665f', grainColor: '#4a4640', dir: 'y' },
  { id: 'lam_walnut_y',    label: 'Dark Walnut ↕',       category: 'laminate', baseColor: '#4a3020', grainColor: '#2c1a0e', dir: 'y' },
  { id: 'lam_walnut_g_y',  label: 'Walnut High-Gloss ↕', category: 'laminate', baseColor: '#5a3a24', grainColor: '#3a2214', dir: 'y' },
];

export const WALL_MATERIALS = [
  // ── NEUTRAL ───────────────────────────────────────────────────────────────
  { id: 'white',       label: 'Pure White',        category: 'neutral', color: '#fafaf8' },
  { id: 'cream',       label: 'Warm Cream',        category: 'neutral', color: '#f0e8d8' },
  { id: 'offwhite',    label: 'Off White',         category: 'neutral', color: '#ece6dc' },
  { id: 'grey_lt',     label: 'Light Grey',        category: 'neutral', color: '#d4d2ce' },
  { id: 'grey_mid',    label: 'Mid Grey',          category: 'neutral', color: '#9a9898' },
  { id: 'charcoal',    label: 'Charcoal',          category: 'neutral', color: '#3c3c44' },
  { id: 'black',       label: 'Matte Black',       category: 'neutral', color: '#141418' },
  // ── COLOUR ────────────────────────────────────────────────────────────────
  { id: 'sage',        label: 'Sage Green',        category: 'colour',  color: '#8faa8b' },
  { id: 'forest',      label: 'Forest Green',      category: 'colour',  color: '#2a5030' },
  { id: 'olive',       label: 'Olive',             category: 'colour',  color: '#707840' },
  { id: 'teal',        label: 'Teal',              category: 'colour',  color: '#2a7878' },
  { id: 'slate_blue',  label: 'Slate Blue',        category: 'colour',  color: '#6a8ca8' },
  { id: 'navy',        label: 'Navy Blue',         category: 'colour',  color: '#1a2a4a' },
  { id: 'indigo',      label: 'Indigo',            category: 'colour',  color: '#2a2860' },
  { id: 'blush',       label: 'Blush',             category: 'colour',  color: '#e0a898' },
  { id: 'dusty_rose',  label: 'Dusty Rose',        category: 'colour',  color: '#c08888' },
  { id: 'burgundy',    label: 'Burgundy',          category: 'colour',  color: '#681828' },
  { id: 'ochre',       label: 'Ochre',             category: 'colour',  color: '#c8a040' },
  { id: 'terracotta',  label: 'Terracotta',        category: 'colour',  color: '#c06840' },
  { id: 'caramel',     label: 'Caramel',           category: 'colour',  color: '#b88048' },
  // ── TEXTURE ───────────────────────────────────────────────────────────────
  { id: 'concrete_w',  label: 'Raw Concrete',      category: 'texture', color: '#a8a8a4' },
  { id: 'plaster',     label: 'Venetian Plaster',  category: 'texture', color: '#ddd4c4' },
  { id: 'wood_panel',  label: 'Wood Panelling',    category: 'texture', color: '#a07848', grainColor: '#886030' },
  { id: 'limewash',    label: 'Limewash',          category: 'texture', color: '#dcd4c4' },
  // ── PATTERN ───────────────────────────────────────────────────────────────
  { id: 'stripe_bw',   label: 'B&W Stripes',       category: 'pattern', color: '#e0e0de', stripe: true, stripeColor: '#2a2a2a' },
  { id: 'stripe_blue', label: 'Navy Stripes',      category: 'pattern', color: '#e8eef5', stripe: true, stripeColor: '#1a3a6a' },
  { id: 'stripe_sage', label: 'Sage Stripes',      category: 'pattern', color: '#f0ede8', stripe: true, stripeColor: '#5a7858' },
  { id: 'wainscot',    label: 'Wainscoting',       category: 'pattern', color: '#f0ece6', wainscot: true, panelColor: '#ddd6cc' },
  // ── MORE NEUTRAL ─────────────────────────────────────────────────────────
  { id: 'greige',      label: 'Greige',            category: 'neutral', color: '#c8c0b0' },
  { id: 'linen',       label: 'Linen',             category: 'neutral', color: '#f0ece0' },
  { id: 'stone_grey',  label: 'Stone Grey',        category: 'neutral', color: '#b8b4ae' },
  { id: 'putty',       label: 'Putty',             category: 'neutral', color: '#d4ccba' },
  // ── MORE COLOUR ──────────────────────────────────────────────────────────
  { id: 'emerald',     label: 'Emerald',           category: 'colour',  color: '#1a5c3a' },
  { id: 'aubergine',   label: 'Aubergine',         category: 'colour',  color: '#4a1848' },
  { id: 'rust',        label: 'Rust',              category: 'colour',  color: '#a84020' },
  { id: 'pine_green',  label: 'Pine Green',        category: 'colour',  color: '#1c4a2a' },
  { id: 'deep_teal',   label: 'Deep Teal',         category: 'colour',  color: '#1a5050' },
  { id: 'burnt_org',   label: 'Burnt Orange',      category: 'colour',  color: '#c85820' },
  // ── MORE TEXTURE ─────────────────────────────────────────────────────────
  { id: 'grasscloth',  label: 'Grasscloth',        category: 'texture', color: '#c8b880' },
  { id: 'cork_wall',   label: 'Cork Wall',         category: 'texture', color: '#c0a870' },
  { id: 'raked_plast', label: 'Raked Plaster',     category: 'texture', color: '#d8cfc0' },
  // ── MORE PATTERN ─────────────────────────────────────────────────────────
  { id: 'stripe_gold', label: 'Gold Stripes',      category: 'pattern', color: '#f5f0e0', stripe: true, stripeColor: '#c89820' },
  { id: 'herring_wp',  label: 'Herringbone WP',    category: 'pattern', color: '#e8e4d8', stripe: true, stripeColor: '#b8a888' },
  // ── STONE / MARBLE ────────────────────────────────────────────────────────
  { id: 'marble_w',    label: 'White Marble',       category: 'texture', color: '#f0eee8', veins: true },
  { id: 'marble_blk',  label: 'Black Marble',       category: 'texture', color: '#1a1a1e', veins: true },
  { id: 'travertine',  label: 'Travertine',         category: 'texture', color: '#d8ccb4' },
  // ── MODERN ────────────────────────────────────────────────────────────────
  { id: 'micro_cem',   label: 'Micro Cement',       category: 'texture', color: '#8c8a86' },
  // ── PANELS ────────────────────────────────────────────────────────────────
  { id: 'fluted',      label: 'Fluted Panels',      category: 'texture', color: '#e0dcd6' },
  // ── FABRIC / NATURAL ──────────────────────────────────────────────────────
  { id: 'fabric_w',    label: 'Linen Weave',        category: 'texture', color: '#cec4b4' },
  { id: 'jute_wall',   label: 'Jute / Sisal',       category: 'texture', color: '#c8b880' },
  // ── NEW: TEXTURE / PATTERN ───────────────────────────────────────────────
  { id: 'venetian_stucco', label: 'Venetian Stucco',    category: 'texture', color: '#d8cdb8' },
  { id: 'board_batten',    label: 'Board & Batten',     category: 'texture', color: '#eae4d6', panelColor: '#d8d0bc' },
  { id: 'bookmatched',     label: 'Bookmatched Marble', category: 'texture', color: '#e8e4dc', veins: true },
  { id: 'chevron_panel',   label: 'Chevron Panel',      category: 'texture', color: '#a87850', grainColor: '#8a5c38' },
  { id: 'pegboard',        label: 'Pegboard',           category: 'texture', color: '#c8a878' },
  // ── NEW MODERN FINISHES (trowelled plaster family, board-formed concrete, ──
  //    3D panels, brushed metal) ─────────────────────────────────────────────
  { id: 'tadelakt',        label: 'Tadelakt',           category: 'texture', color: '#e0d6c4' },
  { id: 'roman_clay',      label: 'Roman Clay',         category: 'texture', color: '#c8bba8' },
  { id: 'clay_plaster',    label: 'Clay Plaster',       category: 'texture', color: '#c99a7c' },
  { id: 'board_concrete',  label: 'Board-Formed Concrete', category: 'texture', color: '#a6a29c' },
  { id: 'panel_3d',        label: '3D Panels',          category: 'texture', color: '#e6e2da' },
  { id: 'brushed_brass',   label: 'Brushed Brass',      category: 'texture', color: '#b8934e' },
  // ── MORE MODERN: wood slat/tambour, terrazzo, charred wood, corten, cane, steel ──
  { id: 'wood_slat',       label: 'Acoustic Wood Slat', category: 'texture', color: '#a07850', grainColor: '#6e4a2a' },
  { id: 'tambour',         label: 'Tambour Wood',       category: 'texture', color: '#b88a5a', grainColor: '#8a5c38' },
  { id: 'terrazzo_w',      label: 'Terrazzo',           category: 'texture', color: '#e8e4dc' },
  { id: 'shou_sugi',       label: 'Shou Sugi Ban',      category: 'texture', color: '#26221e' },
  { id: 'corten',          label: 'Corten Steel',       category: 'texture', color: '#8a5030' },
  { id: 'cane_web',        label: 'Cane Webbing',       category: 'texture', color: '#cbb385' },
  { id: 'brushed_steel',   label: 'Brushed Steel',      category: 'texture', color: '#b0b2b6' },
];

export const CEILING_MATERIALS = [
  // ── PLAIN (painted — the realistic default; each gets a subtle paint stipple) ─
  { id: 'white',        label: 'Matte White',       category: 'plain',   color: '#f6f4ef' },
  { id: 'bright_white', label: 'Bright White',      category: 'plain',   color: '#fbfbf9' },
  { id: 'warm_white',   label: 'Warm White',        category: 'plain',   color: '#f2ece0' },
  { id: 'light_grey',   label: 'Light Grey',        category: 'plain',   color: '#dbd9d4' },
  { id: 'greige',       label: 'Greige',            category: 'plain',   color: '#c9c1b3' },
  { id: 'charcoal',     label: 'Charcoal',          category: 'plain',   color: '#3a3a40' },
  // ── TEXTURE (modern, realistic — reuse the detailed procedural generators) ──
  { id: 'plaster_c',    label: 'Smooth Plaster',    category: 'texture', color: '#e8e4de' },
  { id: 'concrete_ceil',label: 'Exposed Concrete',  category: 'texture', color: '#a8a6a2' },
  { id: 'wood_ceil',    label: 'Wood Plank',        category: 'texture', color: '#b98a58', grainColor: '#916034' },
  { id: 'slat_ceil',    label: 'Wood Slat',         category: 'texture', color: '#b8895c', grainColor: '#9a6c3e' },
  { id: 'plank_ceil',   label: 'Tongue & Groove',   category: 'texture', color: '#ece6da', grainColor: '#d0c8b8' },
  { id: 'coffered',     label: 'Coffered',          category: 'texture', color: '#eeeae2' },
  { id: 'exposed_beam', label: 'Wood Beams',        category: 'texture', color: '#b98a58', grainColor: '#916034' },
  // ── MORE MODERN TEXTURES ────────────────────────────────────────────────────
  { id: 'microcement_c',label: 'Microcement',       category: 'texture', color: '#b4aca0' },
  { id: 'tadelakt_c',   label: 'Tadelakt',          category: 'texture', color: '#e0d6c4' },
  { id: 'fluted_c',     label: 'Fluted Panel',      category: 'texture', color: '#e6e2da' },
  { id: 'wood_white_c', label: 'Whitewashed Wood',  category: 'texture', color: '#d8cdb8', grainColor: '#b8a888' },
  { id: 'wood_dark_c',  label: 'Walnut Wood',       category: 'texture', color: '#5a3f2a', grainColor: '#3c281a' },
  { id: 'beam_white_c', label: 'Painted Beams',     category: 'texture', color: '#ece6da', grainColor: '#d0c8b8' },
  { id: 'herring_c',    label: 'Herringbone Wood',  category: 'texture', color: '#b98a58', grainColor: '#8a5c38' },
  { id: 'bamboo_c',     label: 'Bamboo Reed',       category: 'texture', color: '#cdb87a', grainColor: '#ab944c' },
];

const MATERIAL_LISTS = { floor: FLOOR_MATERIALS, wall: WALL_MATERIALS, ceiling: CEILING_MATERIALS };

// ─── STYLE + CUSTOM-COLOR OVERRIDE ─────────────────────────────────────────────
// A design can be a bare preset id ('oak'), a fully custom flat material
// (__custom: true, from the manual panel with nothing preset selected), or —
// this — a preset's own pattern/texture kept intact but recoloured (and
// optionally refinished) by hand (__colorOverride: true). Rendering code
// (textures.js) is driven purely by mat.id + its hex-colour fields, so
// shifting every hex field on the preset by the same delta as the user's new
// colour reproduces the exact same pattern generator (herringbone, wood
// grain, marble veins, …) in the new colour. A chosen `finish` is carried
// along and applied on top as a roughness/metalness override (finishToPBR in
// textures.js) — the pattern drawing itself never changes.
function hexToRgb(hex) {
  const n = parseInt((hex || '#888888').replace('#', ''), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}
function clamp255(v) { return Math.max(0, Math.min(255, Math.round(v))); }
function rgbToHex(r, g, b) {
  return '#' + [r, g, b].map(v => clamp255(v).toString(16).padStart(2, '0')).join('');
}
export function buildStyleColorOverride(layer, styleId, newColor, finish, label) {
  const base = (MATERIAL_LISTS[layer] || []).find(m => m.id === styleId);
  if (!base) return null;
  const primaryKey = base.baseColor !== undefined ? 'baseColor' : 'color';
  const oldRgb = hexToRgb(base[primaryKey]);
  const newRgb = hexToRgb(newColor);
  const delta = { r: newRgb.r - oldRgb.r, g: newRgb.g - oldRgb.g, b: newRgb.b - oldRgb.b };
  const out = { ...base, __colorOverride: true, [primaryKey]: newColor };
  for (const key of Object.keys(base)) {
    if (key === primaryKey) continue;
    const v = base[key];
    if (typeof v === 'string' && v[0] === '#') {
      const rgb = hexToRgb(v);
      out[key] = rgbToHex(rgb.r + delta.r, rgb.g + delta.g, rgb.b + delta.b);
    }
  }
  if (finish) out.finish = finish;
  if (label) out.label = label;
  return out;
}

// ─── DASHBOARD (GRID) SIZE ────────────────────────────────────────────────────
// Chosen once when a project is created, saved to Firestore as a 'S'/'M'/'L'
// code (see workspaceService.createProject), and applied on load via
// loadDesign's gridSize field / the setGridSize reducer.
export const GRID_SIZES = { S: 50, M: 100, L: 150 };
export const DEFAULT_GRID_SIZE_CODE = 'S';
export function gridSizeCodeToCells(code) {
  return GRID_SIZES[code] || GRID_SIZES[DEFAULT_GRID_SIZE_CODE];
}

// ─── INITIAL STATE ────────────────────────────────────────────────────────────
const initialState = {
  phase: 'build',          // 'build' | 'plan' | 'design'
  gridSize: GRID_SIZES[DEFAULT_GRID_SIZE_CODE], // grid columns x rows — set at project creation, see GRID_SIZES
  cellSize: 56,            // px per cell (for builder)
  nextOpeningId: 1,
  dragFurnitureType: null,
  furniture: [],
  nextFurnitureId: 1,
  lighting: [],
  nextLightingId: 1,

  // Placed rooms: { id, type, label, x, y, w, h, floor/wall/ceiling }
  rooms: [],
  nextId: 1,

  // Builder state
  dragRoomType: null,       // type being dragged from palette
  hoverCell: null,          // {col, row} current hover in grid

  // Plan / Design state
  selectedFloor: 0,         // future: multi-storey
  selectedRoomId: null,
  designTab: 'floor',       // 'floor' | 'walls' | 'ceiling'

  // Design choices per room: { roomId: { floor, wall, ceiling } }
  designs: {},

  // Freehand wall segments: { id, x1, y1, x2, y2, openings: [] } in grid intersection coords (0..gridSize)
  wallSegments: [],
  nextWallId: 1,
  nextWallOpeningId: 1,

  // Free-FORM walls: any angle + optional curve. Kept SEPARATE from wallSegments
  // (which are grid-aligned H/V and feed room auto-detection / openings) so the
  // free-form geometry never destabilises that grid logic.
  //   { id, x1, y1, x2, y2, bulge, thickness }
  //   x/y in float grid-cell units; bulge = perpendicular sagitta in cells
  //   (0 = straight, sign = which side it bows); thickness in cells.
  freeWalls: [],
  nextFreeWallId: 1,

  // Free-form ROOMS: a closed loop of free walls turned into a room with a
  // floor. { id, pts: [{x,y,bulge}], floor }  (pts in cell units; bulge = curve
  // of the edge from this vertex to the next; floor = material id/object).
  freeRooms: [],
  nextFreeRoomId: 1,
  autoWallOpenings: {},   // key "h:y:x1:x2" | "v:x:y1:y2" → [{id,type,t,width,height,color,...}]

  // Builder layout history for Back/Next navigation (rooms, furniture, openings, walls)
  builderHistory: [{ rooms: [], furniture: [], nextId: 1, nextFurnitureId: 1, nextOpeningId: 1, wallSegments: [], nextWallId: 1, nextWallOpeningId: 1, autoWallOpenings: {}, lighting: [], nextLightingId: 1 }],
  builderHistoryIndex: 0,

  // Global wall height in metres (used by all 3D views)
  wallHeight: 2.8,
};

// Deep snapshot of the builder layout (rooms, furniture, counters)
function snapshotBuilder(state) {
  return {
    rooms: state.rooms.map(r => ({
      ...r,
      rects: (r.rects || []).map(rc => ({ ...rc })),
      openings: (r.openings || []).map(o => ({ ...o })),
    })),
    furniture: state.furniture.map(f => ({ ...f })),
    nextId: state.nextId,
    nextFurnitureId: state.nextFurnitureId,
    nextOpeningId: state.nextOpeningId,
    wallSegments: (state.wallSegments || []).map(w => ({ ...w, openings: (w.openings || []).map(o => ({ ...o })) })),
    nextWallId: state.nextWallId || 1,
    nextWallOpeningId: state.nextWallOpeningId || 1,
    freeWalls: (state.freeWalls || []).map(w => ({ ...w })),
    nextFreeWallId: state.nextFreeWallId || 1,
    freeRooms: (state.freeRooms || []).map(r => ({ ...r, pts: (r.pts || []).map(p => ({ ...p })) })),
    nextFreeRoomId: state.nextFreeRoomId || 1,
    autoWallOpenings: Object.fromEntries(
      Object.entries(state.autoWallOpenings || {}).map(([k, ops]) => [k, ops.map(o => ({ ...o }))])
    ),
    lighting: (state.lighting || []).map(l => ({ ...l })),
    nextLightingId: state.nextLightingId || 1,
  };
}

// Pushes a snapshot AFTER applying a change, truncates forward history
function pushBuilderHistory(state) {
  state.builderHistory = state.builderHistory.slice(0, state.builderHistoryIndex + 1);
  state.builderHistory.push(snapshotBuilder(state));
  state.builderHistoryIndex = state.builderHistory.length - 1;
  if (state.builderHistory.length > 60) {
    state.builderHistory.shift();
    state.builderHistoryIndex = state.builderHistory.length - 1;
  }
}

// Restores rooms/furniture from a saved snapshot
function restoreBuilderSnapshot(state, snap) {
  state.rooms = snap.rooms.map(r => ({
    ...r,
    rects: (r.rects || []).map(rc => ({ ...rc })),
    openings: (r.openings || []).map(o => ({ ...o })),
  }));
  state.furniture = snap.furniture.map(f => ({ ...f }));
  state.nextId = snap.nextId;
  state.nextFurnitureId = snap.nextFurnitureId;
  state.nextOpeningId = snap.nextOpeningId;
  state.wallSegments = (snap.wallSegments || []).map(w => ({ ...w, openings: (w.openings || []).map(o => ({ ...o })) }));
  state.nextWallId = snap.nextWallId || 1;
  state.nextWallOpeningId = snap.nextWallOpeningId || 1;
  state.freeWalls = (snap.freeWalls || []).map(w => ({ ...w }));
  state.nextFreeWallId = snap.nextFreeWallId || 1;
  state.freeRooms = (snap.freeRooms || []).map(r => ({ ...r, pts: (r.pts || []).map(p => ({ ...p })) }));
  state.nextFreeRoomId = snap.nextFreeRoomId || 1;
  state.autoWallOpenings = Object.fromEntries(
    Object.entries(snap.autoWallOpenings || {}).map(([k, ops]) => [k, ops.map(o => ({ ...o }))])
  );
  state.lighting = (snap.lighting || []).map(l => ({ ...l }));
  state.nextLightingId = snap.nextLightingId || 1;
}

// Computes the set of continuous exterior/shared wall segments for the current
// room layout — same algorithm as Builder.jsx's `autoWalls` memo (the UI's list
// of clickable walls). Collinear unit-cell edges from adjacent rooms merge into
// one continuous wall regardless of room type, so a wall's key (and therefore
// its length/position) changes whenever room adjacency changes.
// Exported so other placement UIs (e.g. ThreeDRoom's "Add Opening" modal) can
// resolve a room's real wall geometry instead of assuming a simple rectangle.
export function computeRoomAutoWalls(rooms) {
  // Free-form rooms (smooth `pts` outline) are EXCLUDED: their walls follow the
  // real angled/curved outline, whereas tracing their rasterised cells would
  // produce a staircase of 1-cell wall steps around the curve.
  rooms = (rooms || []).filter(r => !(r.pts && r.pts.length >= 3));
  const edgeMap = new Map();
  const addEdge = (x1, y1, x2, y2) => {
    const isH = y1 === y2;
    const key = isH
      ? `h:${y1}:${Math.min(x1, x2)}:${Math.max(x1, x2)}`
      : `v:${x1}:${Math.min(y1, y2)}:${Math.max(y1, y2)}`;
    if (!edgeMap.has(key)) {
      edgeMap.set(key, {
        x1: isH ? Math.min(x1, x2) : x1,
        y1: isH ? y1 : Math.min(y1, y2),
        x2: isH ? Math.max(x1, x2) : x1,
        y2: isH ? y1 : Math.max(y1, y2),
      });
    }
  };

  for (const room of rooms) {
    const cells = new Set();
    const addCells = (rx, ry, rw, rh) => {
      for (let cy = ry; cy < ry + rh; cy++)
        for (let cx = rx; cx < rx + rw; cx++)
          cells.add(`${cx},${cy}`);
    };
    if (room.rects && room.rects.length > 0) room.rects.forEach(r => addCells(r.x, r.y, r.w, r.h));
    else addCells(room.x, room.y, room.w, room.h);

    for (const key of cells) {
      const [cx, cy] = key.split(',').map(Number);
      if (!cells.has(`${cx},${cy - 1}`)) addEdge(cx, cy, cx + 1, cy);
      if (!cells.has(`${cx + 1},${cy}`)) addEdge(cx + 1, cy, cx + 1, cy + 1);
      if (!cells.has(`${cx},${cy + 1}`)) addEdge(cx, cy + 1, cx + 1, cy + 1);
      if (!cells.has(`${cx - 1},${cy}`)) addEdge(cx, cy, cx, cy + 1);
    }
  }

  const rawSegs = Array.from(edgeMap.values());
  const hRaw = rawSegs.filter(s => s.y1 === s.y2).sort((a, b) => a.y1 - b.y1 || a.x1 - b.x1);
  const mergeH = [];
  for (const s of hRaw) {
    const last = mergeH[mergeH.length - 1];
    if (last && last.y1 === s.y1 && last.x2 === s.x1) last.x2 = s.x2;
    else mergeH.push({ ...s });
  }
  const vRaw = rawSegs.filter(s => s.x1 === s.x2).sort((a, b) => a.x1 - b.x1 || a.y1 - b.y1);
  const mergeV = [];
  for (const s of vRaw) {
    const last = mergeV[mergeV.length - 1];
    if (last && last.x1 === s.x1 && last.y2 === s.y1) last.y2 = s.y2;
    else mergeV.push({ ...s });
  }

  const keyOf = (w) => (w.y1 === w.y2 ? `h:${w.y1}:${w.x1}:${w.x2}` : `v:${w.x1}:${w.y1}:${w.y2}`);
  return [...mergeH, ...mergeV].map(w => ({ ...w, key: keyOf(w) }));
}

// One-time repair for room.openings saved before moveOpening (below) learned
// to keep absX/absY in sync: dragging a step-wall opening used to strip
// absX/absY and leave only _wallGz/_wallGx (a 3D-only field), which Builder's
// 2D view and the Plan page don't understand — they'd fall back to the room's
// bounding-box edge, which usually isn't a real wall on a stepped room. Runs
// on every load so already-saved projects self-heal without manual re-placing.
function repairStepWallOpenings(state) {
  const ABS_CELL = 26; // matches ThreeDRoom.jsx's CELL for step-wall openings
  for (const room of state.rooms) {
    const rects = (room.rects && room.rects.length > 0) ? room.rects : null;
    if (!rects || rects.length <= 1) continue;
    for (const op of (room.openings || [])) {
      if (op.absX !== undefined && op.absY !== undefined) continue; // already fine
      if (op._wallGz != null) {
        op.absX = op.pos * room.w * ABS_CELL;
        op.absY = (op._wallGz + room.h / 2) * ABS_CELL;
      } else if (op._wallGx != null) {
        op.absX = (op._wallGx + room.w / 2) * ABS_CELL;
        op.absY = op.pos * room.h * ABS_CELL;
      }
    }
  }
}

// Removes autoWallOpenings that sit on the room's own boundary — including a
// wall shared with a neighboring room — since those openings belong to a wall
// of the room being deleted. Matches by absolute position (like
// reconcileAutoWallOpenings) rather than exact key, so it still finds them
// even if the wall is currently merged with a neighbor's under a wider key.
function removeRoomBoundaryOpenings(state, room) {
  if (!state.autoWallOpenings) return;
  const ownWalls = computeRoomAutoWalls([room]); // this room's boundary, as if it stood alone
  if (!ownWalls.length) return;

  const next = { ...state.autoWallOpenings };
  for (const [key, ops] of Object.entries(next)) {
    if (!ops || !ops.length) continue;
    const parts = key.split(':');
    const isH = parts[0] === 'h';
    const pos = Number(parts[1]), lo = Number(parts[2]), hi = Number(parts[3]);
    const len = hi - lo;
    if (len <= 0) continue;

    const keep = ops.filter(op => {
      if (op.roomId === room.id) return false; // explicitly bound to this room
      const worldPos = lo + op.t * len;
      const onOwnWall = ownWalls.some(w =>
        (isH ? w.y1 === w.y2 : w.x1 === w.x2) &&
        (isH ? w.y1 === pos  : w.x1 === pos) &&
        worldPos >= (isH ? w.x1 : w.y1) - 0.02 && worldPos <= (isH ? w.x2 : w.y2) + 0.02
      );
      return !onOwnWall;
    });

    if (keep.length !== ops.length) {
      if (keep.length) next[key] = keep;
      else delete next[key];
    }
  }
  state.autoWallOpenings = next;
}

// Carries furniture, lighting, and standalone wall segments along with a room
// when it's dragged in the 2D Builder — otherwise those items are placed in
// absolute grid coordinates with no roomId, so moving the room used to leave
// them behind. `oldRects` is the room's footprint *before* the move (in grid
// cells); dxCells/dyCells is the move delta, also in grid cells. Furniture and
// lighting live in the same pixel space as the grid (26px per cell, matching
// Builder.jsx's CELL) but store absolute pixel x/y, so their delta needs
// converting; wall segments already share the room's grid-cell units.
function shiftRoomContents(state, oldRects, dxCells, dyCells) {
  if (!dxCells && !dyCells) return;
  const ABS_CELL = 26; // matches Builder.jsx's CELL
  const dxPx = dxCells * ABS_CELL, dyPx = dyCells * ABS_CELL;
  const inOldRects = (px, py) => oldRects.some(r =>
    px >= r.x * ABS_CELL && px <= (r.x + r.w) * ABS_CELL &&
    py >= r.y * ABS_CELL && py <= (r.y + r.h) * ABS_CELL
  );
  for (const item of state.furniture) {
    const cx = item.x + item.w / 2, cy = item.y + item.h / 2;
    if (inOldRects(cx, cy)) { item.x += dxPx; item.y += dyPx; }
  }
  for (const item of state.lighting) {
    const cx = item.x + item.w / 2, cy = item.y + item.h / 2;
    if (inOldRects(cx, cy)) { item.x += dxPx; item.y += dyPx; }
  }
  for (const seg of (state.wallSegments || [])) {
    if (!segInRects(seg, oldRects)) continue;
    seg.x1 += dxCells; seg.x2 += dxCells; seg.y1 += dyCells; seg.y2 += dyCells;
  }
  // Free walls standing inside the room travel with it too — the straight
  // segments above already did, and a free wall left behind would be stranded in
  // empty space while the room it partitions moved away.
  for (const w of (state.freeWalls || [])) {
    if (!freeWallInRects(w, oldRects)) continue;
    w.x1 += dxCells; w.x2 += dxCells; w.y1 += dyCells; w.y2 += dyCells;
  }
}

// Does this point fall inside any of these cells? The containment test every
// "is it in the room" check below shares.
const ptInRects = (x, y, rects) =>
  rects.some(r => x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h);

// Is a straight wall segment's middle inside the room?
function segInRects(seg, rects) {
  return ptInRects((seg.x1 + seg.x2) / 2, (seg.y1 + seg.y2) / 2, rects);
}

// Is a free wall's MIDDLE inside the room? Uses the arc midpoint, not the
// chord's, so a strongly bowed wall is judged by where it actually runs.
function freeWallInRects(w, rects) {
  const m = edgePointAt({ x: w.x1, y: w.y1, bulge: w.bulge || 0 }, { x: w.x2, y: w.y2 }, 0.5);
  return ptInRects(m.x, m.y, rects);
}

// Keeps autoWallOpenings in sync with the current wall layout (see
// computeRoomAutoWalls above). Whenever room adjacency changes — a room is
// added, moved, resized, or removed near another room — walls can merge or
// split, leaving previously-placed doors/windows filed under a key that no
// longer matches any current wall. Those entries still exist in state (so
// Plan/3D views, which match openings by absolute position rather than exact
// key, keep showing them), but the Builder's 2D wall list only shows entries
// under the *current* key — so they'd appear to vanish there. This finds
// those stale entries and refiles them under the wall they now belong to,
// preserving their relative position along the wall.
function reconcileAutoWallOpenings(state) {
  if (!state.autoWallOpenings) return;
  const currentWalls = computeRoomAutoWalls(state.rooms);
  const currentKeys  = new Set(currentWalls.map(w => w.key));
  const staleKeys = Object.keys(state.autoWallOpenings)
    .filter(k => !currentKeys.has(k) && (k.startsWith('h:') || k.startsWith('v:')));
  if (!staleKeys.length) return;

  const next = { ...state.autoWallOpenings };
  for (const staleKey of staleKeys) {
    const ops = next[staleKey];
    if (!ops || !ops.length) { delete next[staleKey]; continue; }
    const parts = staleKey.split(':');
    const isH = parts[0] === 'h';
    const pos = Number(parts[1]), lo = Number(parts[2]), hi = Number(parts[3]);
    const len = hi - lo;
    if (len <= 0) continue;

    const keep = [];
    for (const op of ops) {
      const worldPos = lo + op.t * len;
      const target = currentWalls.find(w =>
        (isH ? w.y1 === w.y2 : w.x1 === w.x2) &&
        (isH ? w.y1 === pos  : w.x1 === pos) &&
        worldPos >= (isH ? w.x1 : w.y1) - 0.02 && worldPos <= (isH ? w.x2 : w.y2) + 0.02
      );
      if (!target) { keep.push(op); continue; } // no current wall covers it — leave it rather than lose it
      const newLen = isH ? target.x2 - target.x1 : target.y2 - target.y1;
      const newLo  = isH ? target.x1 : target.y1;
      const newT   = newLen > 0 ? Math.max(0.02, Math.min(0.98, (worldPos - newLo) / newLen)) : op.t;
      if (!next[target.key]) next[target.key] = [];
      next[target.key] = [...next[target.key], { ...op, t: newT }];
    }
    if (keep.length) next[staleKey] = keep;
    else delete next[staleKey];
  }
  state.autoWallOpenings = next;
}

// Auto-wall openings (doors/windows placed with the wall tool) are keyed by
// the wall's absolute grid position, e.g. "h:Y:X1:X2". Resizing a room moves
// its walls, so those keys go stale and the openings silently stop matching
// any rendered wall. This carries them over to the new wall position,
// preserving their relative (t) position along the wall.
function remapAutoWallOpeningsForResize(state, roomId, oldRect, newRect) {
  if (!state.autoWallOpenings) return;
  const sides = [
    { isH: true,  old: { pos: oldRect.y,             lo: oldRect.x, hi: oldRect.x + oldRect.w }, neu: { pos: newRect.y,             lo: newRect.x, hi: newRect.x + newRect.w } },
    { isH: true,  old: { pos: oldRect.y + oldRect.h,  lo: oldRect.x, hi: oldRect.x + oldRect.w }, neu: { pos: newRect.y + newRect.h,  lo: newRect.x, hi: newRect.x + newRect.w } },
    { isH: false, old: { pos: oldRect.x,              lo: oldRect.y, hi: oldRect.y + oldRect.h }, neu: { pos: newRect.x,              lo: newRect.y, hi: newRect.y + newRect.h } },
    { isH: false, old: { pos: oldRect.x + oldRect.w,  lo: oldRect.y, hi: oldRect.y + oldRect.h }, neu: { pos: newRect.x + newRect.w,  lo: newRect.y, hi: newRect.y + newRect.h } },
  ];

  const next = { ...state.autoWallOpenings };
  for (const side of sides) {
    const oldLen = side.old.hi - side.old.lo;
    if (oldLen <= 0) continue;
    const oldKey = side.isH
      ? `h:${side.old.pos}:${side.old.lo}:${side.old.hi}`
      : `v:${side.old.pos}:${side.old.lo}:${side.old.hi}`;
    const ops = next[oldKey];
    if (!ops || !ops.length) continue;

    const keep = [];
    const moved = [];
    const newLen = side.neu.hi - side.neu.lo;
    for (const op of ops) {
      // Openings explicitly bound to a different room aren't this room's to remap.
      if (op.roomId != null && op.roomId !== roomId) { keep.push(op); continue; }
      if (newLen <= 0) continue; // wall collapsed — nowhere to put it
      const f = op.t; // fraction along the wall, resize-invariant by definition
      moved.push({ ...op, t: Math.max(0.02, Math.min(0.98, f)) });
    }

    if (keep.length) next[oldKey] = keep;
    else delete next[oldKey];

    if (moved.length) {
      const newKey = side.isH
        ? `h:${side.neu.pos}:${side.neu.lo}:${side.neu.hi}`
        : `v:${side.neu.pos}:${side.neu.lo}:${side.neu.hi}`;
      next[newKey] = [...(next[newKey] || []), ...moved];
    }
  }
  state.autoWallOpenings = next;
}

const houseSlice = createSlice({
  name: 'house',
  initialState,
  reducers: {
    // ── BUILDER ──────────────────────────────────────────────────────────────
    setDragRoomType(state, { payload }) {
      state.dragRoomType = payload;
    },
    setHoverCell(state, { payload }) {
      state.hoverCell = payload;
    },
    placeRoom(state, { payload }) {
      // payload: { type, col, row }
      const rt = ROOM_TYPES.find(r => r.type === payload.type);
      if (!rt) return;
      const newRect = { x: payload.col, y: payload.row, w: rt.w, h: rt.h };
      const nr = { type: rt.type, label: rt.label, ...newRect, rects: [newRect], openings: [] };
      // Bounds check — guard BEFORE consuming nextId so failed placements leave no ID holes
      if (nr.x < 0 || nr.y < 0 || nr.x + nr.w > state.gridSize || nr.y + nr.h > state.gridSize) return;

      const rectsOverlap = (a, b) => a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

      // Block if the new rect overlaps any existing room, regardless of type —
      // rooms no longer auto-merge, so overlapping placement is simply rejected.
      const blocksOverlap = state.rooms.some(r => {
        const rs = (r.rects && r.rects.length > 0) ? r.rects : [{ x: r.x, y: r.y, w: r.w, h: r.h }];
        return rs.some(rc => rectsOverlap(newRect, rc));
      });
      if (blocksOverlap) return;

      const id = state.nextId++;
      nr.id = id;
      state.rooms.push(nr);
      state.designs[id] = { floor: null, wall: null, ceiling: null };
      reconcileAutoWallOpenings(state);
      pushBuilderHistory(state);
    },
    placeCustomRoom(state, { payload }) {
      // payload: { type, x, y, w, h } — explicit grid dimensions (not preset sizes)
      const rt = ROOM_TYPES.find(r => r.type === payload.type) || ROOM_TYPES[0];
      const w  = Math.max(1, Math.round(+payload.w));
      const h  = Math.max(1, Math.round(+payload.h));
      const x  = Math.max(0, Math.min(state.gridSize - w, Math.round(+payload.x)));
      const y  = Math.max(0, Math.min(state.gridSize - h, Math.round(+payload.y)));
      const newRect = { x, y, w, h };
      const nr = { type: rt.type, label: rt.label, x, y, w, h, rects: [newRect], openings: [] };

      const rectsOverlap = (a, b) => a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

      // Block if the new rect overlaps any existing room, regardless of type —
      // rooms no longer auto-merge, so overlapping placement is simply rejected.
      const blocksOverlap = state.rooms.some(r => {
        const rs = (r.rects && r.rects.length > 0) ? r.rects : [{ x: r.x, y: r.y, w: r.w, h: r.h }];
        return rs.some(rc => rectsOverlap(newRect, rc));
      });
      if (blocksOverlap) return;

      const id = state.nextId++;
      nr.id = id;
      state.rooms.push(nr);
      state.designs[id] = { floor: null, wall: null, ceiling: null };
      reconcileAutoWallOpenings(state);
      pushBuilderHistory(state);
    },
    removeRoom(state, { payload }) {
      const room = state.rooms.find(r => r.id === payload);
      // room.openings (the room's own doors/windows) are dropped for free since
      // they live on the room object itself. Auto-wall openings live in a
      // separate global map keyed by wall position, so they need explicit cleanup.
      if (room) {
        removeRoomBoundaryOpenings(state, room);
        // Interior walls standing inside the room go with it — free and straight
        // alike. They only make sense as partitions of that room, and would
        // otherwise be left floating in empty space, taking their openings (and
        // their wall area in the cost estimate) along with them.
        const rects = (room.rects && room.rects.length) ? room.rects : [{ x: room.x, y: room.y, w: room.w, h: room.h }];
        state.freeWalls    = (state.freeWalls || []).filter(w => !freeWallInRects(w, rects));
        state.wallSegments = (state.wallSegments || []).filter(s => !segInRects(s, rects));
      }
      state.rooms = state.rooms.filter(r => r.id !== payload);
      delete state.designs[payload];
      reconcileAutoWallOpenings(state);
      pushBuilderHistory(state);
    },
    addOpening(state, { payload }) {
      // payload: { roomId, type, wall, pos, width?, height?, sill?, style?, absX?, absY? }
      const room = state.rooms.find(r => r.id === payload.roomId);
      if (!room) return;
      if (!room.openings) room.openings = [];
      const defaults = openingTypeDefaults(payload.type);
      const sizeOverrides = {};
      if (payload.width  != null) sizeOverrides.width  = payload.width;
      if (payload.height != null) sizeOverrides.height = payload.height;
      if (payload.sill   != null) sizeOverrides.sill   = payload.sill;
      if (payload.color  != null) sizeOverrides.color  = payload.color; // lets "paste" keep the copied item's color
      if (payload.style  != null) sizeOverrides.style  = payload.style; // ditto, and for moving between walls
      const absPos = {};
      if (payload.absX != null) { absPos.absX = payload.absX; absPos.absY = payload.absY; }
      // Free-form rooms position openings along an outline edge (edge + t)
      // instead of a rectangular wall side.
      const freePos = {};
      if (payload.edge != null) { freePos.edge = payload.edge; freePos.t = payload.t; }
      room.openings.push({ id: state.nextOpeningId++, type: payload.type, wall: payload.wall, pos: payload.pos, ...defaults, ...sizeOverrides, ...absPos, ...freePos });
      pushBuilderHistory(state);
    },
    removeOpening(state, { payload }) {
      // payload: { roomId, openingId }
      const room = state.rooms.find(r => r.id === payload.roomId);
      if (!room) return;
      room.openings = room.openings.filter(o => o.id !== payload.openingId);
      pushBuilderHistory(state);
    },
    moveRoom(state, { payload }) {
      // payload: { id, x, y }  — x,y = new bounding-box top-left
      const idx = state.rooms.findIndex(r => r.id === payload.id);
      if (idx === -1) return;
      const room = state.rooms[idx];
      const newX = Math.max(0, payload.x);
      const newY = Math.max(0, payload.y);
      const dx = newX - room.x, dy = newY - room.y;
      const rects = room.rects || [{ x: room.x, y: room.y, w: room.w, h: room.h }];
      const newRects = rects.map(r => ({ x: r.x + dx, y: r.y + dy, w: r.w, h: r.h }));
      // Bounds check
      if (newRects.some(r => r.x < 0 || r.y < 0 || r.x + r.w > state.gridSize || r.y + r.h > state.gridSize)) return;

      const rectsOverlap = (a, b) => a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

      // Block if any new rect overlaps another room, regardless of type — rooms
      // no longer auto-merge, so an overlapping drag target is simply rejected.
      const blocksOverlap = newRects.some(nr =>
        state.rooms.some(r => {
          if (r.id === room.id) return false;
          const theirRects = (r.rects && r.rects.length > 0) ? r.rects : [{ x: r.x, y: r.y, w: r.w, h: r.h }];
          return theirRects.some(tr => rectsOverlap(nr, tr));
        })
      );
      if (blocksOverlap) return;

      // No overlap — just move
      shiftRoomContents(state, rects, dx, dy);
      state.rooms[idx].rects = newRects;
      state.rooms[idx].x = newX;
      state.rooms[idx].y = newY;
      // A free-form room's smooth outline must travel with it, or the drawn
      // shape would stay behind while its rects (and everything derived from
      // them) moved.
      if (state.rooms[idx].pts) {
        state.rooms[idx].pts = state.rooms[idx].pts.map(p => ({ ...p, x: p.x + dx, y: p.y + dy }));
      }
      // A merged free-form room also carries the straight parts it absorbed —
      // they must travel too, or the shape tears apart as it moves.
      if (state.rooms[idx].mergeRects) {
        state.rooms[idx].mergeRects = state.rooms[idx].mergeRects.map(r => ({ ...r, x: r.x + dx, y: r.y + dy }));
      }

      // Remap autoWallOpenings whose wall positions matched the room's OLD rects
      // to the equivalent wall positions after the move, preserving relative t.
      if (state.autoWallOpenings && (dx !== 0 || dy !== 0)) {
        const newAutoWall = {};
        for (const [key, ops] of Object.entries(state.autoWallOpenings)) {
          if (!ops?.length) continue;
          const parts = key.split(':');
          const isH = parts[0] === 'h';
          const toKeep = [];
          for (const op of ops) {
            // Skip openings explicitly bound to a different room
            if (op.roomId != null && op.roomId !== room.id) { toKeep.push(op); continue; }
            let moved = false;
            if (isH) {
              const Y = Number(parts[1]), X1 = Number(parts[2]), X2 = Number(parts[3]);
              const worldX = X1 + op.t * (X2 - X1);
              for (const r of rects) {
                if ((Y === r.y || Y === r.y + r.h) && worldX >= r.x - 0.01 && worldX <= r.x + r.w + 0.01) {
                  const newY2 = Y + dy, newX1 = r.x + dx, newX2 = r.x + r.w + dx;
                  const newKey = `h:${newY2}:${newX1}:${newX2}`;
                  const newT = Math.max(0.02, Math.min(0.98, (worldX - r.x) / r.w));
                  if (!newAutoWall[newKey]) newAutoWall[newKey] = [];
                  newAutoWall[newKey].push({ ...op, t: newT });
                  moved = true; break;
                }
              }
            } else {
              const X = Number(parts[1]), Y1 = Number(parts[2]), Y2 = Number(parts[3]);
              const worldY = Y1 + op.t * (Y2 - Y1);
              for (const r of rects) {
                if ((X === r.x || X === r.x + r.w) && worldY >= r.y - 0.01 && worldY <= r.y + r.h + 0.01) {
                  const newX2 = X + dx, newY1 = r.y + dy, newY22 = r.y + r.h + dy;
                  const newKey = `v:${newX2}:${newY1}:${newY22}`;
                  const newT = Math.max(0.02, Math.min(0.98, (worldY - r.y) / r.h));
                  if (!newAutoWall[newKey]) newAutoWall[newKey] = [];
                  newAutoWall[newKey].push({ ...op, t: newT });
                  moved = true; break;
                }
              }
            }
            if (!moved) toKeep.push(op);
          }
          if (toKeep.length > 0) {
            if (!newAutoWall[key]) newAutoWall[key] = [];
            newAutoWall[key].push(...toKeep);
          }
        }
        state.autoWallOpenings = newAutoWall;
      }

      // Final pass: catches regrouping caused by adjacency changes with OTHER
      // rooms (not just this room's own walls), which the translation-based
      // remap above doesn't account for.
      reconcileAutoWallOpenings(state);

      pushBuilderHistory(state);
    },
    resizeRoom(state, { payload }) {
      // payload: { id, w, h }
      const room = state.rooms.find(r => r.id === payload.id);
      if (!room) return;
      const newW = Math.max(1, payload.w);
      const newH = Math.max(1, payload.h);
      if (room.x + newW > state.gridSize || room.y + newH > state.gridSize) return;
      const newRect = { x: room.x, y: room.y, w: newW, h: newH };
      const overlaps = state.rooms.some(r => {
        if (r.id === room.id) return false;
        const theirRects = (r.rects && r.rects.length > 0) ? r.rects : [{ x: r.x, y: r.y, w: r.w, h: r.h }];
        return theirRects.some(tr =>
          newRect.x < tr.x + tr.w && newRect.x + newRect.w > tr.x &&
          newRect.y < tr.y + tr.h && newRect.y + newRect.h > tr.y
        );
      });
      if (overlaps) return;
      const oldRect = { x: room.x, y: room.y, w: room.w, h: room.h };
      // A free-form room scales its smooth outline with the bounding box, and
      // keeps its rasterised rects derived from that outline (rather than being
      // flattened to one rectangle, which would destroy the shape).
      if (room.pts && room.pts.length >= 3) {
        const sx = newW / (room.w || 1), sy = newH / (room.h || 1);
        room.pts = room.pts.map(p => ({
          ...p,
          x: room.x + (p.x - room.x) * sx,
          y: room.y + (p.y - room.y) * sy,
          bulge: (p.bulge || 0) * ((sx + sy) / 2),
        }));
        if (room.mergeRects) {
          room.mergeRects = room.mergeRects.map(r => ({
            x: room.x + (r.x - room.x) * sx, y: room.y + (r.y - room.y) * sy,
            w: r.w * sx, h: r.h * sy,
          }));
        }
        room.w = newW; room.h = newH;
        const rr = polygonToRects(room.pts, state.gridSize);
        if (rr.length) room.rects = rr;
        remapAutoWallOpeningsForResize(state, room.id, oldRect, { x: room.x, y: room.y, w: newW, h: newH });
        reconcileAutoWallOpenings(state);
        pushBuilderHistory(state);
        return;
      }
      room.w = newW;
      room.h = newH;
      // Keep rects in sync — resize only applies to single-rect rooms
      room.rects = [{ x: room.x, y: room.y, w: newW, h: newH }];
      remapAutoWallOpeningsForResize(state, room.id, oldRect, newRect);
      reconcileAutoWallOpenings(state);
      pushBuilderHistory(state);
    },
    resizeRoomCorner(state, { payload }) {
      // payload: { id, x, y, w, h } — corner drag changes position AND size atomically
      const idx = state.rooms.findIndex(r => r.id === payload.id);
      if (idx === -1) return;
      const room = state.rooms[idx];
      const newX = Math.max(0, payload.x);
      const newY = Math.max(0, payload.y);
      const newW = Math.max(1, payload.w);
      const newH = Math.max(1, payload.h);
      if (newX + newW > state.gridSize || newY + newH > state.gridSize) return;
      const newRect = { x: newX, y: newY, w: newW, h: newH };
      const overlaps = state.rooms.some(r => {
        if (r.id === room.id) return false;
        const theirRects = (r.rects && r.rects.length > 0) ? r.rects : [{ x: r.x, y: r.y, w: r.w, h: r.h }];
        return theirRects.some(tr =>
          newRect.x < tr.x + tr.w && newRect.x + newRect.w > tr.x &&
          newRect.y < tr.y + tr.h && newRect.y + newRect.h > tr.y
        );
      });
      if (overlaps) return;
      const oldRect = { x: room.x, y: room.y, w: room.w, h: room.h };
      // Free-form room: map its smooth outline from the old bbox onto the new
      // one (so a corner drag scales/repositions the real shape), then rebuild
      // the rasterised rects from that outline instead of one flat rectangle.
      if (room.pts && room.pts.length >= 3) {
        const sx = newW / (room.w || 1), sy = newH / (room.h || 1);
        const pts = room.pts.map(p => ({
          ...p,
          x: newX + (p.x - room.x) * sx,
          y: newY + (p.y - room.y) * sy,
          bulge: (p.bulge || 0) * ((sx + sy) / 2),
        }));
        state.rooms[idx].pts = pts;
        if (room.mergeRects) {
          state.rooms[idx].mergeRects = room.mergeRects.map(r => ({
            x: newX + (r.x - room.x) * sx, y: newY + (r.y - room.y) * sy,
            w: r.w * sx, h: r.h * sy,
          }));
        }
        state.rooms[idx].x = newX;
        state.rooms[idx].y = newY;
        state.rooms[idx].w = newW;
        state.rooms[idx].h = newH;
        const rr = polygonToRects(pts, state.gridSize);
        state.rooms[idx].rects = rr.length ? rr : [newRect];
        remapAutoWallOpeningsForResize(state, room.id, oldRect, newRect);
        reconcileAutoWallOpenings(state);
        return;
      }
      state.rooms[idx].x = newX;
      state.rooms[idx].y = newY;
      state.rooms[idx].w = newW;
      state.rooms[idx].h = newH;
      state.rooms[idx].rects = [newRect];
      remapAutoWallOpeningsForResize(state, room.id, oldRect, newRect);
      reconcileAutoWallOpenings(state);
    },
    clearAllRooms(state) {
      state.rooms = [];
      state.designs = {};
      state.furniture = [];
      state.lighting = [];
      state.wallSegments = [];
      // Free walls / free rooms were missed here, so "clear all" left them
      // floating on an otherwise empty grid.
      state.freeWalls = [];
      state.freeRooms = [];
      state.nextFreeWallId = 1;
      state.nextFreeRoomId = 1;
      state.autoWallOpenings = {};
      state.nextId = 1;
      state.nextFurnitureId = 1;
      state.nextLightingId = 1;
      state.nextOpeningId = 1;
      state.nextWallId = 1;
      state.nextWallOpeningId = 1;
      pushBuilderHistory(state);
    },
    renameRoom(state, { payload }) {
      // payload: { id, label }
      const room = state.rooms.find(r => r.id === payload.id);
      if (room) { room.label = payload.label; pushBuilderHistory(state); }
    },
    addWallSegment(state, { payload }) {
      // payload: { x1, y1, x2, y2 } — grid intersection coordinates 0..gridSize
      // Normalize: horizontal walls always have x1<x2, vertical walls always have y1<y2
      let { x1, y1, x2, y2 } = payload;
      if (x1 === x2 && y1 > y2) { const tmp = y1; y1 = y2; y2 = tmp; }
      if (y1 === y2 && x1 > x2) { const tmp = x1; x1 = x2; x2 = tmp; }
      const newSeg = { x1: +x1, y1: +y1, x2: +x2, y2: +y2 };
      // Reject the segment if it overlaps an existing one on the same line
      const overlaps = (a, b) => {
        const aIsH = a.y1 === a.y2, bIsH = b.y1 === b.y2;
        if (aIsH !== bIsH) return false;
        if (aIsH) return a.y1 === b.y1 && a.x1 < b.x2 && b.x1 < a.x2;
        return a.x1 === b.x1 && a.y1 < b.y2 && b.y1 < a.y2;
      };
      if (state.wallSegments.some(s => overlaps(newSeg, s))) return;
      const id = state.nextWallId++;
      state.wallSegments.push({ id, ...newSeg });
      pushBuilderHistory(state);
    },
    removeWallSegment(state, { payload }) {
      // payload: wall segment id
      state.wallSegments = state.wallSegments.filter(w => w.id !== payload);
      pushBuilderHistory(state);
    },
    clearWallSegments(state) {
      state.wallSegments = [];
      state.nextWallId = 1;
      pushBuilderHistory(state);
    },
    updateWallSegment(state, { payload }) {
      // payload: { id, x1, y1, x2, y2 }
      const seg = state.wallSegments.find(w => w.id === payload.id);
      if (!seg) return;
      let { x1, y1, x2, y2 } = payload;
      if (x1 === x2 && y1 > y2) { const tmp = y1; y1 = y2; y2 = tmp; }
      if (y1 === y2 && x1 > x2) { const tmp = x1; x1 = x2; x2 = tmp; }
      seg.x1 = +x1; seg.y1 = +y1;
      seg.x2 = +x2; seg.y2 = +y2;
      pushBuilderHistory(state);
    },

    // ── FREE-FORM WALLS (any angle + optional curve) ──────────────────────────
    addFreeWall(state, { payload }) {
      // payload: { x1, y1, x2, y2, bulge?, thickness? }
      const { x1, y1, x2, y2, bulge = 0, thickness = 0.16 } = payload;
      if (Math.hypot(x2 - x1, y2 - y1) < 0.05) return; // ignore zero-length
      state.freeWalls.push({
        id: state.nextFreeWallId++,
        x1: +x1, y1: +y1, x2: +x2, y2: +y2,
        bulge: +bulge, thickness: +thickness,
      });
      pushBuilderHistory(state);
    },
    // ── Openings on a standalone FREE wall ───────────────────────────────────
    // Same shape as a straight interior wall's (`wallSegments`) so a free wall
    // edits like one, and sharing nextWallOpeningId keeps ids unique across both.
    // Position is `t` along the wall (0..1) — a free wall runs at any angle and
    // may curve, so there's no "x% of a horizontal run" to store.
    addFreeWallOpening(state, action) {
      const { wallId, type, t, width, height, sill, color, style, side } = action.payload;
      const wall = state.freeWalls.find(w => w.id === wallId);
      if (!wall) return;
      if (!wall.openings) wall.openings = [];
      const defaults = openingTypeDefaults(type);
      const ov = {};
      if (width  != null) ov.width  = width;
      if (height != null) ov.height = height;
      if (sill   != null) ov.sill   = sill;
      if (color  != null) ov.color  = color;
      if (style  != null) ov.style  = style;
      if (side   != null) ov.side   = side;   // which face a wall-decor item mounts on
      wall.openings.push({ id: state.nextWallOpeningId++, type, t, ...defaults, ...ov });
      pushBuilderHistory(state);
    },
    removeFreeWallOpening(state, action) {
      const { wallId, openingId } = action.payload;
      const wall = state.freeWalls.find(w => w.id === wallId);
      if (!wall) return;
      wall.openings = (wall.openings || []).filter(o => o.id !== openingId);
      pushBuilderHistory(state);
    },
    updateFreeWallOpening(state, action) {
      const { wallId, openingId, changes } = action.payload;
      const wall = state.freeWalls.find(w => w.id === wallId);
      const op = wall?.openings?.find(o => o.id === openingId);
      if (!op) return;
      Object.assign(op, changes);
      pushBuilderHistory(state);
    },
    updateFreeWall(state, { payload }) {
      // payload: { id, ...changes } — any of x1/y1/x2/y2/bulge/thickness
      const w = state.freeWalls.find(f => f.id === payload.id);
      if (!w) return;
      for (const k of ['x1', 'y1', 'x2', 'y2', 'bulge', 'thickness']) {
        if (payload[k] !== undefined) w[k] = +payload[k];
      }
      pushBuilderHistory(state);
    },
    removeFreeWall(state, { payload }) {
      state.freeWalls = (state.freeWalls || []).filter(w => w.id !== payload);
      pushBuilderHistory(state);
    },
    clearFreeWalls(state) {
      state.freeWalls = [];
      state.nextFreeWallId = 1;
      pushBuilderHistory(state);
    },

    // ── FREE-FORM ROOMS (closed loop of free walls → room + floor) ────────────
    addFreeRoom(state, { payload }) {
      // payload: { pts: [{x,y,bulge}], floor?, consumedWallIds?, consumedSegIds? }
      const { pts, floor = null, consumedWallIds = [], consumedSegIds = [] } = payload;
      if (!Array.isArray(pts) || pts.length < 3) return;
      state.freeRooms.push({
        id: state.nextFreeRoomId++,
        pts: pts.map(p => ({ x: +p.x, y: +p.y, bulge: +(p.bulge || 0) })),
        floor,
      });
      // The loop's walls now belong to the room's outline — remove the loose
      // free walls AND grid wall segments it consumed so nothing draws twice.
      if (consumedWallIds.length) {
        const drop = new Set(consumedWallIds);
        state.freeWalls = state.freeWalls.filter(w => !drop.has(w.id));
      }
      if (consumedSegIds.length) {
        const drop = new Set(consumedSegIds);
        state.wallSegments = state.wallSegments.filter(w => !drop.has(w.id));
      }
      pushBuilderHistory(state);
    },
    setFreeRoomDesign(state, { payload }) {
      // payload: { id, floor?, wall? } — material id (or object)
      const r = state.freeRooms.find(f => f.id === payload.id);
      if (!r) return;
      if (payload.floor !== undefined) r.floor = payload.floor;
      if (payload.wall !== undefined) r.wall = payload.wall;
      pushBuilderHistory(state);
    },
    moveFreeRoom(state, { payload }) {
      // payload: { id, dx, dy } — translate the whole outline (cell units)
      const r = state.freeRooms.find(f => f.id === payload.id);
      if (!r) return;
      r.pts = r.pts.map(p => ({ ...p, x: p.x + payload.dx, y: p.y + payload.dy }));
      pushBuilderHistory(state);
    },
    // Resize/reshape: move ONE outline vertex (corner) to a new position.
    // Bow (or straighten) ONE edge of a free-form ROOM's outline. Changing the
    // curve changes the shape's extent, so the rasterised rects and the bounding
    // box are both rebuilt from the new outline — every other room tool reads
    // those, not `pts`.
    setRoomEdgeBulge(state, { payload }) {
      // payload: { roomId, edge, bulge }
      const room = state.rooms.find(r => r.id === payload.roomId);
      if (!room?.pts || !room.pts[payload.edge]) return;
      const prev = room.pts[payload.edge].bulge || 0;
      room.pts[payload.edge].bulge = +payload.bulge || 0;
      const rr = polygonToRects(room.pts, state.gridSize);
      // A curve deep enough to collapse the room would leave it with no cells at
      // all — keep the previous shape rather than destroying the room.
      if (!rr.length) { room.pts[payload.edge].bulge = prev; return; }
      room.rects = rr;
      let bx = Infinity, by = Infinity, bx2 = -Infinity, by2 = -Infinity;
      for (const r of rr) {
        bx = Math.min(bx, r.x); by = Math.min(by, r.y);
        bx2 = Math.max(bx2, r.x + r.w); by2 = Math.max(by2, r.y + r.h);
      }
      room.x = bx; room.y = by; room.w = bx2 - bx; room.h = by2 - by;
      pushBuilderHistory(state);
    },
    moveFreeRoomVertex(state, { payload }) {
      // payload: { id, index, x, y }
      const r = state.freeRooms.find(f => f.id === payload.id);
      if (!r || !r.pts[payload.index]) return;
      r.pts[payload.index].x = +payload.x;
      r.pts[payload.index].y = +payload.y;
      pushBuilderHistory(state);
    },
    // Reshape: change the curve (bulge) of the edge starting at `index`.
    setFreeRoomEdgeBulge(state, { payload }) {
      // payload: { id, index, bulge }
      const r = state.freeRooms.find(f => f.id === payload.id);
      if (!r || !r.pts[payload.index]) return;
      r.pts[payload.index].bulge = +payload.bulge;
      pushBuilderHistory(state);
    },
    // ── Openings on a free room's walls ───────────────────────────────────────
    // Stored on the room as { id, type, edge, t, width, height, sill, color, style }
    // where `edge` is the outline edge index and `t` ∈ (0,1) along that edge.
    addFreeRoomOpening(state, { payload }) {
      const { roomId, edge, t, type } = payload;
      const r = state.freeRooms.find(f => f.id === roomId);
      if (!r) return;
      const d = OPENING_TYPE_DEFAULTS[type] || OPENING_TYPE_DEFAULTS.door;
      if (!r.openings) r.openings = [];
      r.openings.push({
        id: state.nextOpeningId++,
        type, edge, t: Math.max(0.02, Math.min(0.98, t)),
        width: payload.width ?? d.width,
        height: payload.height ?? d.height,
        sill: payload.sill ?? d.sill ?? 0,
        color: payload.color ?? d.color ?? null,
        style: payload.style ?? d.style ?? null,
      });
      pushBuilderHistory(state);
    },
    updateFreeRoomOpening(state, { payload }) {
      const { roomId, openingId, changes } = payload;
      const r = state.freeRooms.find(f => f.id === roomId);
      const op = r && (r.openings || []).find(o => o.id === openingId);
      if (!op) return;
      Object.assign(op, changes);
      if (op.t !== undefined) op.t = Math.max(0.02, Math.min(0.98, op.t));
      pushBuilderHistory(state);
    },
    removeFreeRoomOpening(state, { payload }) {
      const r = state.freeRooms.find(f => f.id === payload.roomId);
      if (!r) return;
      r.openings = (r.openings || []).filter(o => o.id !== payload.openingId);
      pushBuilderHistory(state);
    },
    removeFreeRoom(state, { payload }) {
      state.freeRooms = (state.freeRooms || []).filter(r => r.id !== payload);
      pushBuilderHistory(state);
    },
    clearFreeRooms(state) {
      state.freeRooms = [];
      state.nextFreeRoomId = 1;
      pushBuilderHistory(state);
    },
    addWallSegOpening(state, action) {
      const { wallId, type, t, width, height, sill, color, style, side } = action.payload;
      const wall = state.wallSegments.find(w => w.id === wallId);
      if (!wall) return;
      if (!wall.openings) wall.openings = [];
      const defaults = openingTypeDefaults(type);
      const ov = {};
      if (width  != null) ov.width  = width;
      if (height != null) ov.height = height;
      if (sill   != null) ov.sill   = sill;
      if (color  != null) ov.color  = color; // lets "paste" keep the copied item's color
      if (style  != null) ov.style  = style; // ditto, and for moving between walls
      // Which of the wall's two faces a wall-decor item (painting/AC/clock/
      // outlet/radiator) mounts on — a freestanding interior wall has usable
      // room on both sides, unlike a door/window/archway which cuts through
      // and is visible from either side regardless. Doors etc. ignore this.
      if (side != null) ov.side = side;
      wall.openings.push({ id: state.nextWallOpeningId++, type, t, ...defaults, ...ov });
      pushBuilderHistory(state);
    },
    removeWallSegOpening(state, action) {
      const { wallId, openingId } = action.payload;
      const wall = state.wallSegments.find(w => w.id === wallId);
      if (!wall) return;
      wall.openings = (wall.openings || []).filter(o => o.id !== openingId);
      pushBuilderHistory(state);
    },
    addAutoWallOpening(state, action) {
      const { key, type, t, width, height, sill, color, roomId } = action.payload;
      if (!state.autoWallOpenings) state.autoWallOpenings = {};
      if (!state.autoWallOpenings[key]) state.autoWallOpenings[key] = [];
      const defaults = openingTypeDefaults(type);
      const ov = {};
      if (width  != null) ov.width  = width;
      if (height != null) ov.height = height;
      if (sill   != null) ov.sill   = sill;
      if (color  != null) ov.color  = color; // lets "paste" keep the copied item's color
      const entry = { id: state.nextWallOpeningId++, type, t, ...defaults, ...ov };
      if (roomId != null) entry.roomId = roomId;
      state.autoWallOpenings[key].push(entry);
      pushBuilderHistory(state);
    },
    removeAutoWallOpening(state, action) {
      const { key, openingId } = action.payload;
      if (!state.autoWallOpenings?.[key]) return;
      state.autoWallOpenings[key] = state.autoWallOpenings[key].filter(o => o.id !== openingId);
      pushBuilderHistory(state);
    },
    updateWallSegOpening(state, action) {
      const { wallId, openingId, changes } = action.payload;
      const wall = state.wallSegments.find(w => w.id === wallId);
      const op = wall?.openings?.find(o => o.id === openingId);
      if (!op) return;
      Object.assign(op, changes);
      pushBuilderHistory(state);
    },
    updateAutoWallOpening(state, action) {
      const { key, openingId, changes } = action.payload;
      const op = state.autoWallOpenings?.[key]?.find(o => o.id === openingId);
      if (!op) return;
      Object.assign(op, changes);
      pushBuilderHistory(state);
    },
    placePolygonRoom(state, { payload }) {
      // payload: { type, rects: [{x,y,w,h},...], pts?, consumedWallIds?, consumedSegIds? }
      // Identical room creation pipeline as placeRoom / placeCustomRoom.
      // `pts` (optional) is a free-form outline (angled/curved) kept alongside
      // the rects: the rects make it a REAL room every tool understands, while
      // pts drive the smooth 2D/3D rendering.
      const { type, rects } = payload;
      if (!rects || rects.length === 0) return;
      const rt = ROOM_TYPES.find(r => r.type === type) || ROOM_TYPES.find(r => r.type === 'custom') || ROOM_TYPES[0];
      const id = state.nextId++;

      // Compute bounding box from the rect list
      let bx = Infinity, by = Infinity, bx2 = -Infinity, by2 = -Infinity;
      for (const r of rects) {
        bx = Math.min(bx, r.x); by = Math.min(by, r.y);
        bx2 = Math.max(bx2, r.x + r.w); by2 = Math.max(by2, r.y + r.h);
      }
      const x = bx, y = by, w = bx2 - bx, h = by2 - by;
      if (x < 0 || y < 0 || x + w > state.gridSize || y + h > state.gridSize) return;

      const nr = { id, type: rt.type, label: rt.label, x, y, w, h, rects: rects.map(r => ({ ...r })), openings: [] };
      if (payload.pts && payload.pts.length >= 3) {
        nr.pts = payload.pts.map(p => ({ x: +p.x, y: +p.y, bulge: +(p.bulge || 0) }));
      }

      const rectsOverlap = (a, b) => a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

      // Block if any new rect overlaps a different-type room
      const blocksDiffType = state.rooms.some(er => {
        if (er.type === nr.type) return false;
        const ers = (er.rects && er.rects.length > 0) ? er.rects : [{ x: er.x, y: er.y, w: er.w, h: er.h }];
        return rects.some(nr2 => ers.some(rc => rectsOverlap(nr2, rc)));
      });
      if (blocksDiffType) return;

      state.rooms.push(nr);
      state.designs[id] = { floor: null, wall: null, ceiling: null };
      // Walls that formed this outline are now the room's own walls.
      if (payload.consumedWallIds?.length) {
        const drop = new Set(payload.consumedWallIds);
        state.freeWalls = (state.freeWalls || []).filter(w => !drop.has(w.id));
      }
      if (payload.consumedSegIds?.length) {
        const drop = new Set(payload.consumedSegIds);
        state.wallSegments = (state.wallSegments || []).filter(w => !drop.has(w.id));
      }
      reconcileAutoWallOpenings(state);
      pushBuilderHistory(state);
    },
    mergeRooms(state, { payload }) {
      // payload: [ids] — combine the given rooms into a single (compound) room.
      // The caller only passes ids that actually share a wall (see Builder's
      // roomsShareWall check), so this just unions their rects and re-anchors
      // openings. The first id becomes the surviving/merged room.
      const CELL = 26; // px per grid cell (matches Builder.jsx)
      const ids = Array.isArray(payload) ? payload : [];
      if (ids.length < 2) return;
      const idSet = new Set(ids);
      const targets = state.rooms.filter(r => idSet.has(r.id));
      if (targets.length < 2) return;
      // A DRAWN room (free walls → smooth `pts` outline) can't be merged. Merging
      // one into a rectangle room could only ever keep the two shapes side by
      // side — the outline for the drawn part, cells for the rest — which left
      // the now-internal shared wall drawn in the plan and standing in 3D. The
      // Builder blocks the click; this guards every other caller.
      if (targets.some(r => r.pts && r.pts.length >= 3)) return;

      const getRects = (r) => (r.rects && r.rects.length > 0) ? r.rects : [{ x: r.x, y: r.y, w: r.w, h: r.h }];
      const allRects = targets.flatMap(r => getRects(r).map(rc => ({ x: rc.x, y: rc.y, w: rc.w, h: rc.h })));

      let bx = Infinity, by = Infinity, bx2 = -Infinity, by2 = -Infinity;
      for (const r of allRects) {
        bx = Math.min(bx, r.x); by = Math.min(by, r.y);
        bx2 = Math.max(bx2, r.x + r.w); by2 = Math.max(by2, r.y + r.h);
      }
      const mx = bx, my = by, mw = bx2 - bx, mh = by2 - by;

      // Re-anchor every opening to the merged room's origin as absX/absY (px),
      // so it keeps its real position even though the bounding box changed. An
      // opening that fell on a now-internal shared wall simply won't match any
      // exterior auto-wall and stops rendering.
      const mergedOpenings = [];
      for (const r of targets) {
        for (const op of (r.openings || [])) {
          let wx, wy; // world position in cells
          if (op.absX !== undefined && op.absY !== undefined) {
            wx = r.x + op.absX / CELL; wy = r.y + op.absY / CELL;
          } else {
            const pos = op.pos ?? 0.5;
            if (op.wall === 'top')         { wx = r.x + pos * r.w; wy = r.y; }
            else if (op.wall === 'bottom') { wx = r.x + pos * r.w; wy = r.y + r.h; }
            else if (op.wall === 'left')   { wx = r.x;             wy = r.y + pos * r.h; }
            else                           { wx = r.x + r.w;       wy = r.y + pos * r.h; } // right
          }
          mergedOpenings.push({ ...op, absX: (wx - mx) * CELL, absY: (wy - my) * CELL });
        }
      }

      const base = targets[0];
      const merged = { ...base, x: mx, y: my, w: mw, h: mh, rects: allRects, openings: mergedOpenings };
      // Every merge is rectangle-only now (drawn rooms are rejected above), so
      // the result is described by its cells alone. `mergeRects` is still read by
      // the renderers for designs saved back when mixed merges were allowed.
      delete merged.pts;
      delete merged.mergeRects;

      const result = [];
      for (const r of state.rooms) {
        if (r.id === base.id) result.push(merged);
        else if (!idSet.has(r.id)) result.push(r);
      }
      state.rooms = result;
      for (const id of ids) { if (id !== base.id) delete state.designs[id]; }
      state.selectedRoomId = base.id;
      reconcileAutoWallOpenings(state);
      pushBuilderHistory(state);
    },
    setDragFurnitureType(state, { payload }) {
      state.dragFurnitureType = payload;
    },
    placeFurniture(state, { payload }) {
      // payload: { type, x, y, w?, h?, h3?, rotation?, color?, style? } — the
      // optional overrides let "paste" create an exact duplicate (same size/
      // rotation/color/style as the copied item) in a single dispatch instead
      // of place + resize + rotate + recolor as separate history entries.
      const ft = FURNITURE_TYPES.find(f => f.type === payload.type);
      if (!ft) return;
      const item = {
        id: state.nextFurnitureId++, type: payload.type,
        x: payload.x, y: payload.y,
        w: payload.w ?? ft.w, h: payload.h ?? ft.h,
        h3: payload.h3 ?? ft.h3 ?? 1,
        rotation: payload.rotation ?? 0,
        color: payload.color ?? ft.color,
      };
      if (payload.style) item.style = payload.style;
      // Only carry the "user picked a colour" flag through when it's provided
      // (e.g. paste duplicating an already-recoloured item). A brand-new item
      // stays unflagged, so GLB models render their default look first.
      if (payload.colored) item.colored = true;
      state.furniture.push(item);
      state.dragFurnitureType = null;
      pushBuilderHistory(state);
    },
    moveFurniture(state, { payload }) {
      // payload: { id, x, y, elevation? } — elevation (metres) lifts the item so
      // it sits on top of another item it was dropped onto (0 = on the floor).
      // Only passed for "stackable" items (e.g. plants); omitting it leaves any
      // existing elevation untouched.
      const item = state.furniture.find(f => f.id === payload.id);
      if (!item) return;
      item.x = payload.x;
      item.y = payload.y;
      if (payload.elevation !== undefined) item.elevation = payload.elevation;
      pushBuilderHistory(state);
    },
    rotateFurniture(state, { payload }) {
      // payload: id
      const item = state.furniture.find(f => f.id === payload);
      if (!item) return;
      item.rotation = ((item.rotation || 0) + 90) % 360;
      pushBuilderHistory(state);
    },
    removeFurniture(state, { payload }) {
      state.furniture = state.furniture.filter(f => f.id !== payload);
      pushBuilderHistory(state);
    },
    resizeFurniture(state, { payload }) {
      // payload: { id, w, h, h3? } — stored dimensions (pre-rotation), already
      // clamped by caller. h3 (vertical height, metres) is optional so the
      // existing 2D drag-resize handle (which only ever touches w/h) doesn't
      // need to know about it.
      const item = state.furniture.find(f => f.id === payload.id);
      if (!item) return;
      item.w = Math.max(2.6, payload.w); // 0.1m floor (26px/m)
      item.h = Math.max(2.6, payload.h);
      if (payload.h3 != null) item.h3 = Math.max(0.1, payload.h3);
    },
    changeFurnitureType(state, { payload }) {
      // payload: { id, type }
      const item = state.furniture.find(f => f.id === payload.id);
      if (!item) return;
      const ft = FURNITURE_TYPES.find(f => f.type === payload.type);
      if (!ft) return;
      item.type = ft.type;
      item.w = ft.w;
      item.h = ft.h;
      item.h3 = ft.h3 ?? 1;
      item.rotation = 0;
      pushBuilderHistory(state);
    },
    changeFurnitureStyle(state, { payload }) {
      // payload: { id, style }
      const item = state.furniture.find(f => f.id === payload.id);
      if (!item) return;
      item.style = payload.style;
      // Reset size to the type's default so a newly-picked model shows at its
      // proper size, instead of inheriting the previous model's custom W/L/H —
      // which, fit into a mismatched box, rendered the wrong size. Each GLB is
      // authored to look right at its type's default footprint, so this gives a
      // consistent starting size on every style switch (resize from there).
      const ft = FURNITURE_TYPES.find(f => f.type === item.type);
      if (ft) { item.w = ft.w; item.h = ft.h; if (ft.h3 != null) item.h3 = ft.h3; }
      pushBuilderHistory(state);
    },
    // ── LIGHTING (ceiling-mounted fixtures, 3D room view only) ────────────────
    // Free x/y like furniture, but height-locked to the ceiling plane — see
    // LightingMesh3D / DraggableLighting in ThreeDRoom.jsx for the Y clamp.
    placeLighting(state, { payload }) {
      // payload: { type, x, y, w?, h?, rotation?, color?, style? } — optional
      // overrides let "paste" duplicate an exact copy in one dispatch.
      const lt = LIGHTING_TYPES.find(l => l.type === payload.type);
      if (!lt) return;
      const item = {
        id: state.nextLightingId++, type: payload.type,
        x: payload.x, y: payload.y,
        w: payload.w ?? lt.w, h: payload.h ?? lt.h,
        rotation: payload.rotation ?? 0,
        color: payload.color ?? lt.color,
      };
      if (payload.style) item.style = payload.style;
      state.lighting.push(item);
      pushBuilderHistory(state);
    },
    moveLighting(state, { payload }) {
      // payload: { id, x, y }
      const item = state.lighting.find(l => l.id === payload.id);
      if (!item) return;
      item.x = payload.x;
      item.y = payload.y;
      pushBuilderHistory(state);
    },
    resizeLighting(state, { payload }) {
      // payload: { id, w, h } — already clamped by caller
      const item = state.lighting.find(l => l.id === payload.id);
      if (!item) return;
      item.w = Math.max(2.6, payload.w); // 0.1m floor (26px/m)
      item.h = Math.max(2.6, payload.h);
    },
    rotateLighting(state, { payload }) {
      // payload: id
      const item = state.lighting.find(l => l.id === payload);
      if (!item) return;
      item.rotation = ((item.rotation || 0) + 90) % 360;
      pushBuilderHistory(state);
    },
    removeLighting(state, { payload }) {
      state.lighting = state.lighting.filter(l => l.id !== payload);
      pushBuilderHistory(state);
    },
    changeLightingType(state, { payload }) {
      // payload: { id, type }
      const item = state.lighting.find(l => l.id === payload.id);
      if (!item) return;
      const lt = LIGHTING_TYPES.find(l => l.type === payload.type);
      if (!lt) return;
      item.type = lt.type;
      item.w = lt.w;
      item.h = lt.h;
      delete item.style;
      pushBuilderHistory(state);
    },
    changeLightingStyle(state, { payload }) {
      // payload: { id, style }
      const item = state.lighting.find(l => l.id === payload.id);
      if (!item) return;
      item.style = payload.style;
      // Same as furniture: reset to the fixture type's default footprint so a
      // newly-picked fixture shows at its proper size, not the previous one's.
      const lt = LIGHTING_TYPES.find(l => l.type === item.type);
      if (lt) { item.w = lt.w; item.h = lt.h; }
      pushBuilderHistory(state);
    },
    recolorLighting(state, { payload }) {
      // payload: { id, color }. `colored` records an EXPLICIT pick so the GLB
      // fixture only tints once the user actually changes it (see resetLightingColor).
      const item = state.lighting.find(l => l.id === payload.id);
      if (item) { item.color = payload.color; item.colored = true; pushBuilderHistory(state); }
    },
    resetLightingColor(state, { payload }) {
      // payload: { id } — back to the fixture's authored GLB materials.
      const item = state.lighting.find(l => l.id === payload.id);
      if (item) { item.color = undefined; item.colored = false; pushBuilderHistory(state); }
    },
    goToPhase(state, { payload }) {
      state.phase = payload;
      state.dragRoomType = null;
      state.hoverCell = null;
    },
    // ── DESIGN ───────────────────────────────────────────────────────────────
    selectRoom(state, { payload }) {
      state.selectedRoomId = payload;
      state.phase = 'design';
    },
    backToPlan(state) {
      state.selectedRoomId = null;
      state.phase = 'plan';
    },
    setDesignTab(state, { payload }) {
      state.designTab = payload;
    },
    applyMaterial(state, { payload }) {
      const { roomId, layer, materialId } = payload;
      if (!state.designs[roomId]) state.designs[roomId] = { floor: null, wall: null, ceiling: null };
      state.designs[roomId][layer] = materialId;
    },
    // Keeps a preset's pattern/texture (its id + every derived colour field)
    // but recolours it by hand — see buildStyleColorOverride above.
    overrideStyleColor(state, { payload }) {
      const { roomId, layer, styleId, color, finish, label } = payload;
      const derived = buildStyleColorOverride(layer, styleId, color, finish, label);
      if (!derived) return;
      if (!state.designs[roomId]) state.designs[roomId] = { floor: null, wall: null, ceiling: null };
      state.designs[roomId][layer] = derived;
    },
    setCustomMaterial(state, { payload }) {
      const { roomId, layer, color, finish, label } = payload;
      if (!state.designs[roomId]) state.designs[roomId] = { floor: null, wall: null, ceiling: null };
      state.designs[roomId][layer] = {
        __custom: true,
        id: `__custom_${layer}`,
        label: label || 'Custom',
        color,
        baseColor: color,
        finish: finish || 'matte',
        category: 'custom',
      };
    },
    resetRoomDesign(state, { payload }) {
      state.designs[payload] = { floor: null, wall: null, ceiling: null };
    },
    // Set one layer (floor|wall|ceiling) to any value — a preset id (string), a
    // custom/override object, or null. Used by the per-layer "Back" (undo) to
    // restore a previous material.
    setDesignLayer(state, { payload }) {
      const { roomId, layer, value } = payload;
      if (!state.designs[roomId]) state.designs[roomId] = { floor: null, wall: null, ceiling: null };
      state.designs[roomId][layer] = value ?? null;
    },
    // Per-wall (accent-wall) override. faceId identifies one wall face
    // ('back'|'front'|'left'|'right', or a compound-wall segment key). value is a
    // preset id (string), a custom/override object, or null to clear it (which
    // returns that wall to the room's default wall material).
    setWallFaceMaterial(state, { payload }) {
      const { roomId, faceId, value } = payload;
      if (!state.designs[roomId]) state.designs[roomId] = { floor: null, wall: null, ceiling: null };
      const d = state.designs[roomId];
      if (!d.wallFaces) d.wallFaces = {};
      if (value == null) delete d.wallFaces[faceId];
      else d.wallFaces[faceId] = value;
    },
    // Per-area (metre-grid) wall override. faceId identifies the wall
    // ('back'|'front'|'left'|'right' or a compound segment key); `cells` is a
    // list of grid-cell keys ("col-row"); value is a preset id (string), a
    // custom/override object, or null to clear those cells (returning them to
    // the wall's material). Lets the user recolour a ~1m patch of one wall.
    setWallPatches(state, { payload }) {
      const { roomId, faceId, cells, value } = payload;
      if (!Array.isArray(cells) || !cells.length) return;
      if (!state.designs[roomId]) state.designs[roomId] = { floor: null, wall: null, ceiling: null };
      const d = state.designs[roomId];
      if (!d.wallPatches) d.wallPatches = {};
      if (!d.wallPatches[faceId]) d.wallPatches[faceId] = {};
      const face = d.wallPatches[faceId];
      for (const key of cells) {
        if (value == null) delete face[key];
        else face[key] = value;
      }
      if (Object.keys(face).length === 0) delete d.wallPatches[faceId];
    },
    // Replace the whole per-area override map for a room (used by the wall
    // "Back"/undo to restore all accent patches to a previous snapshot).
    restoreWallPatches(state, { payload }) {
      const { roomId, wallPatches } = payload;
      if (!state.designs[roomId]) state.designs[roomId] = { floor: null, wall: null, ceiling: null };
      const d = state.designs[roomId];
      if (!wallPatches || Object.keys(wallPatches).length === 0) delete d.wallPatches;
      else d.wallPatches = wallPatches;
    },
    // Per-area (metre-grid) FLOOR override. The floor is a single surface, so
    // cells are keyed directly ("col-row") with no faceId. value is a preset id
    // (string), a custom/override object, or null to clear those cells.
    setFloorPatches(state, { payload }) {
      const { roomId, cells, value } = payload;
      if (!Array.isArray(cells) || !cells.length) return;
      if (!state.designs[roomId]) state.designs[roomId] = { floor: null, wall: null, ceiling: null };
      const d = state.designs[roomId];
      if (!d.floorPatches) d.floorPatches = {};
      for (const key of cells) {
        if (value == null) delete d.floorPatches[key];
        else d.floorPatches[key] = value;
      }
      if (Object.keys(d.floorPatches).length === 0) delete d.floorPatches;
    },
    // Restore the whole floor override map (used by the floor "Back"/undo).
    restoreFloorPatches(state, { payload }) {
      const { roomId, floorPatches } = payload;
      if (!state.designs[roomId]) state.designs[roomId] = { floor: null, wall: null, ceiling: null };
      const d = state.designs[roomId];
      if (!floorPatches || Object.keys(floorPatches).length === 0) delete d.floorPatches;
      else d.floorPatches = floorPatches;
    },
    commitBuilderHistory(state) {
      pushBuilderHistory(state);
    },
    builderHistoryBack(state) {
      if (state.builderHistoryIndex > 0) {
        state.builderHistoryIndex--;
        restoreBuilderSnapshot(state, state.builderHistory[state.builderHistoryIndex]);
      }
    },
    builderHistoryNext(state) {
      if (state.builderHistoryIndex < state.builderHistory.length - 1) {
        state.builderHistoryIndex++;
        restoreBuilderSnapshot(state, state.builderHistory[state.builderHistoryIndex]);
      }
    },
    // Jumps straight to a specific history index in one step, rather than
    // walking it one undo at a time — used by the 3D room editor's "Cancel"
    // button to discard everything done since edit mode was entered.
    jumpToBuilderHistory(state, { payload: index }) {
      const clamped = Math.max(0, Math.min(state.builderHistory.length - 1, index));
      state.builderHistoryIndex = clamped;
      restoreBuilderSnapshot(state, state.builderHistory[clamped]);
    },
    resizeOpening(state, { payload }) {
      // payload: { roomId, openingId, width?, height?, sill?, style? }
      const room = state.rooms.find(r => r.id === payload.roomId);
      if (!room) return;
      const op = (room.openings || []).find(o => o.id === payload.openingId);
      if (!op) return;
      // Every item's floor is 0.1m, uniformly — each panel's own UI already
      // enforces this same minimum too.
      if (payload.width  !== undefined) op.width  = Math.max(0.1, Math.min(3.0, payload.width));
      if (payload.height !== undefined) op.height = Math.max(0.1, Math.min(3.5, payload.height));
      if (payload.sill   !== undefined) op.sill   = Math.max(0.0,  Math.min(2.8, payload.sill));
      if (payload.style  !== undefined) op.style  = payload.style;
      pushBuilderHistory(state);
    },
    moveOpening(state, { payload }) {
      // payload: { roomId, openingId, wall, pos, _wallGz?, _wallGx? }
      const room = state.rooms.find(r => r.id === payload.roomId);
      if (!room) return;
      const op = (room.openings || []).find(o => o.id === payload.openingId);
      if (!op) return;
      op.wall = payload.wall;
      op.pos  = Math.max(0.05, Math.min(0.95, payload.pos));
      // Free-form room: the opening rides an outline edge (edge + t), so update
      // that instead of the rectangular wall geometry below.
      if (payload.edge != null) {
        op.edge = payload.edge;
        op.t = Math.max(0.05, Math.min(0.95, payload.t));
        pushBuilderHistory(state);
        return;
      }
      // Persist rect-plane coordinates for compound rooms; clear them for simple rooms
      if (payload._wallGz != null) op._wallGz = payload._wallGz; else delete op._wallGz;
      if (payload._wallGx != null) op._wallGx = payload._wallGx; else delete op._wallGx;

      // _wallGz/_wallGx pin the drag-snap to the exact rect edge it landed on
      // (metres, room-centre-relative — see Opening3D's raycast hit test).
      // Builder's 2D view and the Plan page don't understand that field, only
      // absX/absY, so recompute the equivalent precise position from it for
      // compound rooms rather than leaving them to fall back to the room's
      // bounding-box edge (which usually isn't a real wall on a stepped room).
      const rects = (room.rects && room.rects.length > 0) ? room.rects : null;
      const ABS_CELL = 26; // matches ThreeDRoom.jsx's CELL for step-wall openings
      if (rects && rects.length > 1 && payload._wallGz != null) {
        op.absX = op.pos * room.w * ABS_CELL;
        op.absY = (payload._wallGz + room.h / 2) * ABS_CELL;
      } else if (rects && rects.length > 1 && payload._wallGx != null) {
        op.absX = (payload._wallGx + room.w / 2) * ABS_CELL;
        op.absY = op.pos * room.h * ABS_CELL;
      } else {
        delete op.absX;
        delete op.absY;
      }
      pushBuilderHistory(state);
    },
    moveAutoWallOpening(state, { payload }) {
      // payload: { key, opId, t }  — reposition a builder-placed auto-wall opening along its wall
      const arr = state.autoWallOpenings[payload.key];
      if (!arr) return;
      const op = arr.find(o => o.id === payload.opId);
      if (!op) return;
      op.t = Math.max(0.02, Math.min(0.98, payload.t));
      pushBuilderHistory(state);
    },
    recolorOpening(state, { payload }) {
      // payload: { roomId, openingId, color }. `colored` records an EXPLICIT
      // pick so the GLB door/window/clock only tints once the user changes it.
      const room = state.rooms.find(r => r.id === payload.roomId);
      if (!room) return;
      const op = (room.openings || []).find(o => o.id === payload.openingId);
      if (op) { op.color = payload.color; op.colored = true; pushBuilderHistory(state); }
    },
    resetOpeningColor(state, { payload }) {
      // payload: { roomId, openingId } — back to the item's authored look.
      const room = state.rooms.find(r => r.id === payload.roomId);
      if (!room) return;
      const op = (room.openings || []).find(o => o.id === payload.openingId);
      if (op) { op.color = undefined; op.colored = false; pushBuilderHistory(state); }
    },
    recolorFurniture(state, { payload }) {
      // payload: { id, color }
      const item = state.furniture.find(f => f.id === payload.id);
      // `colored` records that the user has EXPLICITLY chosen a colour. GLB
      // models use it to decide whether to tint: a freshly-placed model shows
      // its own authored materials (the "default") and only takes the picked
      // colour once the user actually changes it.
      if (item) { item.color = payload.color; item.colored = true; pushBuilderHistory(state); }
    },
    resetFurnitureColor(state, { payload }) {
      // payload: { id } — return to the DEFAULT look. Clears the picked colour
      // so procedural furniture falls back to its type colour, and unsets
      // `colored` so GLB models render their authored materials again.
      const item = state.furniture.find(f => f.id === payload.id);
      if (item) { item.color = undefined; item.colored = false; pushBuilderHistory(state); }
    },
    setWallHeight(state, { payload }) {
      state.wallHeight = Math.max(2.2, Math.min(10.0, Number(payload)));
    },

    loadDesign(state, { payload: d }) {
      if (!d || d.version !== 1) return;
      // gridSize isn't part of the saved design JSON — it's set once at project
      // creation and stored as a sibling Firestore field ('S'/'M'/'L'), which the
      // caller (App.jsx) resolves to a cell count and merges into this payload.
      state.gridSize          = d.gridSize           || GRID_SIZES[DEFAULT_GRID_SIZE_CODE];
      state.rooms             = d.rooms             || [];
      repairStepWallOpenings(state);
      state.designs           = d.designs            || {};
      state.furniture         = d.furniture          || [];
      state.lighting          = d.lighting           || [];
      state.wallSegments      = d.wallSegments       || [];
      state.freeWalls         = d.freeWalls          || [];
      state.nextFreeWallId    = d.nextFreeWallId     || 1;
      state.freeRooms         = d.freeRooms          || [];
      state.nextFreeRoomId    = d.nextFreeRoomId     || 1;
      state.autoWallOpenings  = d.autoWallOpenings   || {};
      state.wallHeight        = d.wallHeight         ?? 2.8;
      state.nextId            = d.nextId             || 1;
      state.nextFurnitureId   = d.nextFurnitureId    || 1;
      state.nextLightingId    = d.nextLightingId     || 1;
      state.nextOpeningId     = d.nextOpeningId      || 1;
      state.nextWallId        = d.nextWallId         || 1;
      state.nextWallOpeningId = d.nextWallOpeningId  || 1;
      // Always land on a safe top-level phase; 'design' and 'view3d' require
      // selectedRoomId to be set and would crash if restored with null.
      const safePhase = d.phase === 'build' || d.phase === 'plan' ? d.phase : 'plan';
      state.phase             = (d.rooms && d.rooms.length > 0) ? safePhase : 'build';
      state.selectedRoomId    = null;
      state.builderHistory     = [snapshotBuilder(state)];
      state.builderHistoryIndex = 0;
    },
  },
});

export const {
  setDragRoomType, setHoverCell, placeRoom, placeCustomRoom, removeRoom, addOpening, removeOpening, resizeOpening, recolorOpening, resetOpeningColor, moveOpening,
  moveRoom, resizeRoom, resizeRoomCorner, clearAllRooms, renameRoom, addWallSegment, removeWallSegment, clearWallSegments, updateWallSegment, placePolygonRoom, mergeRooms, goToPhase,
  addFreeWall, updateFreeWall, removeFreeWall, clearFreeWalls,
  addFreeWallOpening, removeFreeWallOpening, updateFreeWallOpening,
  addFreeRoom, setFreeRoomDesign, removeFreeRoom, clearFreeRooms, moveFreeRoom, moveFreeRoomVertex, setFreeRoomEdgeBulge, setRoomEdgeBulge,
  addFreeRoomOpening, updateFreeRoomOpening, removeFreeRoomOpening,
  addWallSegOpening, removeWallSegOpening, addAutoWallOpening, removeAutoWallOpening, moveAutoWallOpening,
  updateWallSegOpening, updateAutoWallOpening,
  setDragFurnitureType, placeFurniture, moveFurniture, rotateFurniture, resizeFurniture, removeFurniture, changeFurnitureType, changeFurnitureStyle, recolorFurniture, resetFurnitureColor,
  placeLighting, moveLighting, rotateLighting, removeLighting, resizeLighting, changeLightingType, changeLightingStyle, recolorLighting, resetLightingColor,
  selectRoom, backToPlan, setDesignTab, applyMaterial, overrideStyleColor, setCustomMaterial, resetRoomDesign, setDesignLayer, setWallFaceMaterial, setWallPatches, restoreWallPatches, setFloorPatches, restoreFloorPatches,
  commitBuilderHistory, builderHistoryBack, builderHistoryNext, jumpToBuilderHistory,
  setWallHeight,
  loadDesign,
} = houseSlice.actions;

export default houseSlice.reducer;
